import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals, getAuthHeaders } from '../shared';
import CascadingServiceSelector from './CascadingServiceSelector';
import { SearchableSelect } from './ui/searchable-select';
import { openServerPdf, askPdfOrXlsx } from '../utils/pdfPrint';
import SubagentApprovalDialog from './SubagentApprovalDialog';
import { GrantCreditNoteDialog } from './CreditNotesManagement';
import SubsequentAdjustmentsPanel from './SubsequentAdjustmentsPanel';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3 } from 'lucide-react';

// 🆕 Age-based pricing helpers (Phase 1) — mirrors backend `_resolve_age_tier`.
// Age brackets (inclusive):
//   • infant (0-2), young_child_no_seat (3-5 no seat), young_child_with_seat
//     (3-5 with seat), child (6-10), teen (11-15), adult (16+).
const AGE_TIER_META = {
  adult:                 { label: '👤 بالغ (+16)',        range: '16+',                     age: 30, has_seat: true  },
  teen:                  { label: '🧑 مراهق (11-15)',     range: '11-15',                   age: 13, has_seat: true  },
  child:                 { label: '🧒 طفل (6-10)',        range: '6-10',                    age: 8,  has_seat: true  },
  young_child_with_seat: { label: '👶 3-5 مع كرسي',       range: '3-5 مع كرسي',            age: 4,  has_seat: true  },
  young_child_no_seat:   { label: '👶 3-5 بدون كرسي',    range: '3-5 بدون كرسي (مجاناً)', age: 4,  has_seat: false },
  infant:                { label: '👶 رضيع (0-2)',        range: '0-2 (مجاناً)',            age: 1,  has_seat: false },
};
const AGE_TIER_ORDER = ['adult','teen','child','young_child_with_seat','young_child_no_seat','infant'];
// Compute integer age (years) from an ISO YYYY-MM-DD birth date.
const ageFromBirthDate = (bd) => {
  if (!bd) return null;
  const d = new Date(bd);
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
  return Math.max(0, a);
};
const resolvePaxTier = (ageOrNull, hasSeat) => {
  if (ageOrNull === null || ageOrNull === undefined || ageOrNull === '') return 'adult';
  const a = parseInt(ageOrNull, 10);
  if (Number.isNaN(a)) return 'adult';
  if (a <= 2) return 'infant';
  if (a <= 5) return hasSeat ? 'young_child_with_seat' : 'young_child_no_seat';
  if (a <= 10) return 'child';
  if (a <= 15) return 'teen';
  return 'adult';
};

const OrganizedTripsManagement = (() => {
  const { user } = useContext(AuthContext);
  const [trips, setTrips] = useState([]);
  const [services, setServices] = useState([]);
  const [hierarchicalServices, setHierarchicalServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [umrahTrips, setUmrahTrips] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  // 🆕 Credit note dialog target — { id, label } when set, opens the discount modal.
  const [creditNoteFor, setCreditNoteFor] = useState(null);
  // 🆕 Quick filters driven by the Dashboard "pending approvals" chips.
  // - 'all'           : show every trip
  // - 'pending_trips' : only trip rows with status pending_approval
  // - 'pending_payments': only trips that have at least one
  //                      pending-approval payment in trip_payments
  const [pendingMode, setPendingMode] = useState('all');
  // Set of trip_ids that currently have a pending payment — populated
  // when pendingMode === 'pending_payments'.
  const [tripsWithPendingPayments, setTripsWithPendingPayments] = useState(null);

  // Read the navigation intent ONCE and pick the matching pending filter.
  useEffect(() => {
    const intent = sessionStorage.getItem('pending-intent');
    if (intent === 'trips') {
      setPendingMode('pending_trips');
      sessionStorage.removeItem('pending-intent');
    } else if (intent === 'trip_payments') {
      setPendingMode('pending_payments');
      sessionStorage.removeItem('pending-intent');
    }
  }, []);

  // When the user enters 'pending_payments' mode, fetch the set of
  // trip_ids that actually have a pending payment row.
  useEffect(() => {
    if (pendingMode !== 'pending_payments') {
      setTripsWithPendingPayments(null);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/pending-approvals/trip-ids`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setTripsWithPendingPayments(new Set(r.data?.trip_ids || []));
      } catch (e) {
        console.warn('failed to load pending trip-ids:', e.message);
        setTripsWithPendingPayments(new Set());
      }
    })();
  }, [pendingMode]);
  
  // Dialog states
  const [showAddTripDialog, setShowAddTripDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTripForPayment, setSelectedTripForPayment] = useState(null);

  // 🆕 2026-07-22: Convert remaining amount to installment plan
  // (mirrors DailyOperations "تحويل إلى الأقساط" workflow).
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertTrip, setConvertTrip] = useState(null);
  const [convertRemaining, setConvertRemaining] = useState(0);
  const [convertForm, setConvertForm] = useState({
    number_of_payments: 3,
    start_date: new Date().toISOString().split('T')[0],
    payment_frequency: 'monthly',
    custom_dates: [],
  });
  const [converting, setConverting] = useState(false);

  const openConvertDialog = (trip) => {
    const remaining = Number(trip.remaining_amount || 0);
    setConvertTrip(trip);
    setConvertRemaining(remaining);
    setConvertForm({
      number_of_payments: 3,
      start_date: new Date().toISOString().split('T')[0],
      payment_frequency: 'monthly',
      custom_dates: [],
    });
    setShowConvertDialog(true);
  };

  const handleConvertSubmit = async () => {
    if (!convertTrip) return;
    if (convertForm.payment_frequency === 'custom') {
      const dates = convertForm.custom_dates || [];
      if (dates.filter(Boolean).length !== Number(convertForm.number_of_payments)) {
        alert('يجب إدخال جميع التواريخ المخصصة');
        return;
      }
    }
    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        number_of_payments: Number(convertForm.number_of_payments),
        start_date: convertForm.start_date,
        payment_frequency: convertForm.payment_frequency,
      };
      if (convertForm.payment_frequency === 'custom') {
        payload.custom_dates = convertForm.custom_dates;
      }
      const r = await axios.post(
        `${API}/organized-trips/${convertTrip.id}/convert-to-installments`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert(
        `✅ ${r.data.message}\n` +
        `رقم الوصل: ${r.data.receipt_no}\n` +
        `المبلغ: ${r.data.amount_converted.toLocaleString()} دج`,
      );
      setShowConvertDialog(false);
      // Optimistic: stamp trip so button hides immediately.
      const _tid = convertTrip.id;
      const _rid = r.data.receipt_id;
      const _rno = r.data.receipt_no;
      setTrips(prev => prev.map(t => (t.id === _tid
        ? { ...t, installment_receipt_id: _rid, installment_receipt_no: _rno, installment_converted: true }
        : t
      )));
      setConvertTrip(null);
      // Background reconcile
      fetchTrips();
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || e.message));
    } finally {
      setConverting(false);
    }
  };
  
  // Form states for new trip
  const [tripForm, setTripForm] = useState({
    trip_type: 'عمرة',
    client_id: 'new_client',
    client_name: '',
    client_phone: '',
    client_email: '',
    client_cin_passport: '',
    service_id: '',
    hotel_id: null,
    room_id: null,
    has_meals: false,
    computed_price: 0,
    service_label: '',
    umrah_trip_id: '',
    trip_date: new Date().toISOString().split('T')[0],
    return_date: '',
    destination: '',
    duration_days: '',
    trip_details: '',
    supplier_name: '',
    supplier_contact: '',
    discount_amount: 0,
    commission_amount: 0,
    discount_reason: '',
    commission_reason: '',
    // 🆕 Supplier integration (Phase 1) — same model as Daily Ops / Tickets
    service_origin: 'internal',   // 'internal' = صنهاجة | 'supplier' = مورد
    supplier_id: '',
    supplier_cost: '',
    // 🆕 Airline discount eligibility — مجاهد / ابن شهيد / أخرى
    discount_category: '',
    discount_document_file: null,
    // 🆕 Age-based pricing (Phase 1) — internal bus / family trips with mixed ages
    pricing_mode: 'fixed',                // 'fixed' | 'age_based'
    age_pricing: {
      adult:                 { sell: '', cost: '' },  // 16+
      teen:                  { sell: '', cost: '' },  // 11-15
      child:                 { sell: '', cost: '' },  // 6-10
      young_child_with_seat: { sell: '', cost: '' },  // 3-5 with seat
      young_child_no_seat:   { sell: 0,  cost: 0  },  // 3-5 no seat — free
      infant:                { sell: 0,  cost: 0  },  // 0-2 — free
    },
    passengers: [],  // [{ id, name, age, has_seat }]
    // 🆕 Age-based pricing v3 (2026-07): the user prefers to enter COUNTS
    // per age tier rather than individual passenger rows (each customer
    // has their own info elsewhere). We keep `passengers` in sync as
    // synthetic entries so backend / trip listing stay untouched.
    age_tier_counts: {
      adult: 0, teen: 0, child: 0,
      young_child_with_seat: 0, young_child_no_seat: 0, infant: 0,
    },
  });

  // 🆕 Suppliers list for the origin picker
  const [suppliers, setSuppliers] = useState([]);
  // 🆕 Flight discount info linked to the chosen umrah_trip — for auto-fill on the financial section
  const [linkedFlightDiscount, setLinkedFlightDiscount] = useState(null);
  
  // Form states for payment
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    notes: '',
    document_file: null
  });
  
  // NEW: Service change states
  const [showChangeServiceDialog, setShowChangeServiceDialog] = useState(false);
  const [selectedTripForServiceChange, setSelectedTripForServiceChange] = useState(null);
  const [serviceChangeForm, setServiceChangeForm] = useState({
    new_service_id: '',
    reason: '',
    // 🆕 'simple' = legacy services dropdown; 'hierarchical' = CascadingServiceSelector
    service_kind: 'simple',
    // 🆕 Required when service_kind='hierarchical' — the room+meals computed total
    computed_price: 0,
    // 🆕 Optional readable label like "عمرة - فندق X - غرفة ثنائية (مع إعاشة)"
    new_service_label: '',
    // Internal — the cascading selector keeps its own intermediate state here.
    hotel_id: null,
    room_id: null,
    has_meals: false,
    // 🆕 Mandatory proof document (image or PDF, ≤5MB) — backend rejects the
    // request without it (HTTP 400).
    document: null,
    // 🆕 Where will the price-difference / refund come from? Used by the
    // approval flow to know which ledger to debit.
    deduction_source: 'cashbox',
  });
  const [serviceChangePrediction, setServiceChangePrediction] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    trip_type: 'all',
    agency_id: 'all',
    start_date: '',
    end_date: '',
    supplier_name: '',
    service_id: 'all',
    payment_status: 'all', // all | paid | partial | unpaid | exceptional_discount
  });
  
  // Customer management states
  const [showNewClientFields, setShowNewClientFields] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  
  // Document states
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  // Document viewing modal states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalImageTitle, setModalImageTitle] = useState('');
  
  // Payment details modal states
  const [showPaymentDetailsDialog, setShowPaymentDetailsDialog] = useState(false);
  const [selectedTripPayments, setSelectedTripPayments] = useState(null);

  // 🆕 Approval / rejection confirmation dialog state — replaces the
  // unreliable window.confirm + window.prompt chains that Chrome
  // silently auto-dismisses after a few uses (the "buttons don't work"
  // complaint). One generic dialog handles all 5 flows:
  //   approve_trip, reject_trip, approve_payment, reject_payment, delete_payment
  const [confirmDialog, setConfirmDialog] = useState(null);
  // confirmDialog shape:
  //   { kind: 'approve_payment'|'reject_payment'|'approve_trip'|'reject_trip'|'delete_payment',
  //     payment?: object, trip?: object,
  //     title, message, askReason: bool, confirmLabel, danger: bool }
  const [confirmReason, setConfirmReason] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [tripPayments, setTripPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Reports modal states
  const [showTripsReportDialog, setShowTripsReportDialog] = useState(false);
  const [showAdjustmentsReportDialog, setShowAdjustmentsReportDialog] = useState(false);
  const [tripsReportData, setTripsReportData] = useState(null);
  const [adjustmentsReportData, setAdjustmentsReportData] = useState(null);

  // Fetch trips data
  const fetchTrips = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query parameters for filtering
      const params = new URLSearchParams();
      if (filters.trip_type && filters.trip_type !== 'all') {
        params.append('trip_type', filters.trip_type);
      }
      if (filters.agency_id && filters.agency_id !== 'all') {
        params.append('agency_id', filters.agency_id);
      }
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      if (filters.supplier_name) {
        params.append('supplier_name', filters.supplier_name);
      }
      if (filters.service_id && filters.service_id !== 'all') {
        params.append('service_id', filters.service_id);
      }
      
      const response = await axios.get(`${API}/organized-trips?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTrips(response.data);
      
    } catch (error) {
      console.error('Error fetching trips:', error);
      alert('فشل في تحميل بيانات الرحلات المنظمة');
    } finally {
      setLoading(false);
    }
  };

  // Fetch services for dropdown
  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const [legacy, hier] = await Promise.all([
        axios.get(`${API}/services?is_active=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/hierarchical-services?is_active=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
      ]);
      setServices(legacy.data || []);
      setHierarchicalServices(hier.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // 🆕 Fetch age_pricing preset from a hierarchical service. Called on
  // service selection. Returns the age_pricing dict or null.
  const fetchServiceAgePricing = async (serviceId) => {
    if (!serviceId) return null;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/hierarchical-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const svc = (r.data || []).find(s => s.id === serviceId);
      return svc?.age_pricing || null;
    } catch (e) {
      return null;
    }
  };

  // 🆕 Role check — who can edit age-price tiers on the trip form.
  // Everyone else sees them as read-only (auto-loaded from the service).
  const canEditAgePricing = user?.role === 'super_admin'
    || user?.role === 'general_accountant'
    || user?.role === 'general_manager'
    || user?.role === 'services_manager';
  // Fetch clients for customer selection
  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  // Fetch agencies for filter (managers/accountants only)
  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  // Handle client selection change
  const handleClientSelectionChange = (clientId) => {
    if (clientId === 'new_client') {
      setShowNewClientFields(true);
      setTripForm({
        ...tripForm,
        client_id: 'new_client',
        client_name: '',
        client_phone: '',
        client_email: '',
        client_cin_passport: ''
      });
    } else {
      const selectedClient = clients.find(c => c.id === clientId);
      if (selectedClient) {
        setShowNewClientFields(false);
        setTripForm({
          ...tripForm,
          client_id: clientId,
          client_name: selectedClient.name,
          client_phone: selectedClient.phone,
          client_email: '',
          client_cin_passport: selectedClient.cin_passport
        });
      }
    }
  };

  // Calculate final amount
  const calculateFinalAmount = () => {
    // 🆕 Age-based pricing: total sell = Σ (count_per_tier × tier.sell)
    // The user removed the per-passenger tab (2026-07); we sum straight
    // from the age_tier_counts grid instead of iterating passengers.
    if (tripForm.pricing_mode === 'age_based') {
      const counts = tripForm.age_tier_counts || {};
      const totalSell = AGE_TIER_ORDER.reduce((sum, tier) => {
        const n = parseInt(counts[tier] || 0, 10);
        const p = parseFloat(tripForm.age_pricing?.[tier]?.sell) || 0;
        return sum + n * p;
      }, 0);
      const discount = parseFloat(tripForm.discount_amount) || 0;
      const commission = parseFloat(tripForm.commission_amount) || 0;
      return Math.round(totalSell - discount + commission);
    }
    // Use hierarchical computed_price if set, else fallback to legacy service.base_price
    let basePrice = 0;
    if (tripForm.computed_price && tripForm.computed_price > 0) {
      basePrice = tripForm.computed_price;
    } else {
      const selectedService = services.find(s => s.id === tripForm.service_id);
      basePrice = selectedService?.base_price || 0;
    }
    const discount = parseFloat(tripForm.discount_amount) || 0;
    const commission = parseFloat(tripForm.commission_amount) || 0;
    return Math.round(basePrice - discount + commission);
  };

  // 🆕 Compute total supplier cost from age_tier_counts × tier.cost.
  // Adult usually has cost === sell (no margin), children carry the
  // supplier discount directly.
  const calculateAgeBasedSupplierCost = () => {
    const counts = tripForm.age_tier_counts || {};
    return AGE_TIER_ORDER.reduce((sum, tier) => {
      const n = parseInt(counts[tier] || 0, 10);
      const c = parseFloat(tripForm.age_pricing?.[tier]?.cost) || 0;
      return sum + n * c;
    }, 0);
  };

  // 🆕 Total head-count across every tier (used for a friendly badge & validation).
  const totalAgeBasedPax = () => {
    const counts = tripForm.age_tier_counts || {};
    return AGE_TIER_ORDER.reduce((s, tier) => s + (parseInt(counts[tier] || 0, 10)), 0);
  };

  // 🆕 Rebuild the `passengers` array as synthetic entries whenever
  // age_tier_counts changes — keeps the backend contract identical
  // (no server-side changes needed) and the "قائمة المسافرين" listing
  // continues to render correctly.
  const bumpTierCount = (tier, delta) => {
    setTripForm((prev) => {
      const cur = parseInt(prev.age_tier_counts?.[tier] || 0, 10);
      const next = Math.max(0, cur + delta);
      const counts = { ...(prev.age_tier_counts || {}), [tier]: next };
      // Rebuild synthetic passengers so backend still receives them.
      const passengers = [];
      for (const t of AGE_TIER_ORDER) {
        const n = parseInt(counts[t] || 0, 10);
        const meta = AGE_TIER_META[t];
        for (let i = 0; i < n; i++) {
          passengers.push({
            id: `p_${t}_${i}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            name: '',
            birth_date: null,
            age: meta.age,
            has_seat: !!meta.has_seat,
          });
        }
      }
      return { ...prev, age_tier_counts: counts, passengers };
    });
  };

  // Create organized trip
  const createTrip = async () => {
    // 🆕 Enforce supplier picker rules first:
    //  - "خدمة مورد" → require a supplier picked from the catalog
    //  - "خدمة صنهاجة" → auto-fill supplier_name so legacy backend doesn't reject
    if (tripForm.service_origin === 'supplier' && !tripForm.supplier_id) {
      alert('❌ اختر مورداً من القائمة (أو غيّر إلى "خدمة صنهاجة")');
      return;
    }
    if (tripForm.service_origin === 'internal' && !tripForm.supplier_name) {
      tripForm.supplier_name = 'صنهاجة (داخلي)';
    }

    // Enhanced validation
    if (!tripForm.trip_type || !tripForm.client_name || !tripForm.service_id || 
        !tripForm.trip_date || !tripForm.destination || !tripForm.supplier_name) {
      alert('يرجى إدخال جميع البيانات المطلوبة');
      return;
    }

    // Validate discount/commission reasons
    if (parseFloat(tripForm.discount_amount) > 0 && !tripForm.discount_reason.trim()) {
      alert('يرجى إدخال سبب التخفيض');
      return;
    }
    
    if (parseFloat(tripForm.commission_amount) > 0 && !tripForm.commission_reason.trim()) {
      alert('يرجى إدخال سبب العمولة');
      return;
    }

    // 🆕 Age-based pricing validations — the passenger tab is gone (2026-07),
    // we now only require at least one head across the age-tier count grid.
    if (tripForm.pricing_mode === 'age_based') {
      const totalPax = totalAgeBasedPax();
      if (totalPax === 0) {
        alert('❌ نظام التسعير حسب الأعمار يتطلب تحديد عدد الأشخاص لشريحة واحدة على الأقل');
        return;
      }
      if (calculateFinalAmount() <= 0) {
        alert('❌ إجمالي سعر البيع للرحلة يساوي صفر — تأكد من إدخال الأسعار لشرائح الأعمار المستخدمة');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      
      const tripData = {
        trip_type: tripForm.trip_type,
        client_id: tripForm.client_id === 'new_client' ? null : tripForm.client_id,
        client_name: tripForm.client_name,
        client_phone: tripForm.client_phone,
        client_email: tripForm.client_email,
        client_cin_passport: tripForm.client_cin_passport,
        service_id: tripForm.service_id,
        // Hierarchical service fields (season 2026)
        hotel_id: tripForm.hotel_id,
        room_id: tripForm.room_id,
        has_meals: tripForm.has_meals,
        computed_price: tripForm.computed_price,
        service_label: tripForm.service_label,
        umrah_trip_id: tripForm.umrah_trip_id || null,
        trip_date: tripForm.trip_date,
        return_date: tripForm.return_date || null,
        destination: tripForm.destination,
        duration_days: parseInt(tripForm.duration_days) || null,
        trip_details: tripForm.trip_details,
        supplier_name: tripForm.supplier_name,
        supplier_contact: tripForm.supplier_contact,
        discount_amount: parseFloat(tripForm.discount_amount) || 0,
        commission_amount: parseFloat(tripForm.commission_amount) || 0,
        discount_reason: tripForm.discount_reason || null,
        commission_reason: tripForm.commission_reason || null,
        // 🆕 Airline discount eligibility — set BEFORE creating, document uploaded after
        discount_category: tripForm.discount_category || null,
        // 🆕 Supplier integration (transparency): persist cost + link so the
        // trips list can show 💰 تكلفة المورد and 📈 الربح for external-supplier
        // trips created by regular agencies (not only subagent approval flow).
        service_origin: tripForm.service_origin || 'internal',
        supplier_id: tripForm.service_origin === 'supplier' ? (tripForm.supplier_id || null) : null,
        supplier_service_id: tripForm.service_origin === 'supplier' ? (tripForm.supplier_service_id || null) : null,
        supplier_cost: tripForm.pricing_mode === 'age_based'
          ? (tripForm.service_origin === 'supplier' ? calculateAgeBasedSupplierCost() : null)
          : (tripForm.service_origin === 'supplier'
              ? (parseFloat(tripForm.supplier_cost) || 0)
              : null),
        // 🆕 Age-based pricing (Phase 1)
        pricing_mode: tripForm.pricing_mode || 'fixed',
        age_pricing: tripForm.pricing_mode === 'age_based' ? (() => {
          // Normalise: convert form strings to numbers so backend gets floats
          const out = {};
          for (const [tier, v] of Object.entries(tripForm.age_pricing || {})) {
            out[tier] = {
              sell: parseFloat(v?.sell) || 0,
              cost: parseFloat(v?.cost) || 0,
            };
          }
          return out;
        })() : null,
        passengers: tripForm.pricing_mode === 'age_based'
          ? (tripForm.passengers || []).map(p => ({
              id: p.id,
              name: (p.name || '').trim(),
              birth_date: p.birth_date || null,
              age: (p.age === '' || p.age === null || p.age === undefined)
                ? (p.birth_date ? ageFromBirthDate(p.birth_date) : null)
                : parseInt(p.age, 10),
              has_seat: !!p.has_seat,
            }))
          : null,
      };

      const response = await axios.post(`${API}/organized-trips`, tripData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 🆕 If a discount document file was attached, upload it now
      const newTripId = response.data.trip_id || response.data.id;
      if (tripForm.discount_category && tripForm.discount_document_file && newTripId) {
        try {
          const fd = new FormData();
          fd.append('document', tripForm.discount_document_file);
          await axios.post(
            `${API}/organized-trips/${newTripId}/upload-discount-document?discount_category=${encodeURIComponent(tripForm.discount_category)}`,
            fd,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
          );
        } catch (upErr) {
          console.warn('discount document upload failed:', upErr.response?.data || upErr.message);
          alert('⚠️ تم إنشاء الرحلة لكن فشل رفع سند التخفيض — يمكنك رفعه لاحقاً.');
        }
      }

      // 🆕 If this organized trip is a supplier resale, register a
      // SALE_DUE in the supplier ledger. Failure is non-blocking.
      if (tripForm.service_origin === 'supplier' && tripForm.supplier_id) {
        // 🆕 Age-based pricing uses the passengers-computed totals; fixed
        // mode falls back to the manually entered supplier_cost / computed_price.
        const cost = tripForm.pricing_mode === 'age_based'
          ? calculateAgeBasedSupplierCost()
          : parseFloat(tripForm.supplier_cost || 0);
        const sell = tripForm.pricing_mode === 'age_based'
          ? calculateFinalAmount()
          : parseFloat(tripForm.computed_price || 0);
        if (cost > 0 && sell > 0) {
          try {
            await axios.post(`${API}/suppliers/_internal/sale-hook`, {
              supplier_id: tripForm.supplier_id,
              supplier_service_id: null,
              operation_id: response.data.trip_id || response.data.id || response.data.trip_no,
              operation_type: 'organized_trip',
              sell_price: sell,
              cost_price: cost,
              client_name: tripForm.client_name || null,
              description: `رحلة منظمة ${response.data.trip_no || ''} - ${tripForm.trip_type}`,
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (hookErr) {
            console.warn('supplier sale-hook (trip) failed:', hookErr.response?.data || hookErr.message);
            alert('⚠️ تم إنشاء الرحلة لكن فشل ربطها بالمورد. راجع كشف حساب المورد لاحقاً.');
          }
        }
      }

      alert(`✅ تم إنشاء الرحلة المنظمة بنجاح!\n\nرقم الرحلة: ${response.data.trip_no}\nرقم العملية اليومية: ${response.data.operation_no}\n\n✨ تم إضافة الرحلة للعمليات اليومية تلقائياً\n💰 يمكنك الآن إضافة الدفعات`);
      
      setShowAddTripDialog(false);
      resetTripForm();
      fetchTrips(); // Refresh trips list
      
    } catch (error) {
      console.error('Error creating trip:', error);
      // 🆕 Surface the smart duplicate warning with an inline confirm so
      // the user can verify before forcing the insert.
      if (
        error.response?.status === 409
        && error.response?.data?.detail?.code === 'DUPLICATE_SUSPECTED'
      ) {
        const det = error.response.data.detail;
        const ex = det.existing || {};
        const human = (
          `${det.message}\n\n` +
          `الرحلة المشابهة:\n` +
          `• الرقم: ${ex.trip_no || '—'}\n` +
          `• الوجهة: ${ex.destination || '—'}\n` +
          `• المبلغ النهائي: ${(ex.final_amount || 0).toLocaleString()} دج\n` +
          `• تاريخ الإنشاء: ${ex.created_at ? new Date(ex.created_at).toLocaleString('ar-DZ') : '—'}\n\n` +
          `هل تريد الإكمال رغم ذلك؟`
        );
        if (window.confirm(human)) {
          try {
            const retry = await axios.post(
              `${API}/organized-trips`,
              { ...tripData, force: true },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            alert(`✅ تم إنشاء الرحلة (تم تجاوز التحذير) — رقم: ${retry.data.trip_no}`);
            setShowAddTripDialog(false);
            resetTripForm();
            fetchTrips();
            return;
          } catch (retryErr) {
            alert('❌ فشل: ' + (retryErr.response?.data?.detail || retryErr.message));
            return;
          }
        }
        return;
      }
      alert('❌ فشل في إنشاء الرحلة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Reset trip form
  const resetTripForm = () => {
    setTripForm({
      trip_type: 'عمرة',
      client_id: 'new_client',
      client_name: '',
      client_phone: '',
      client_email: '',
      client_cin_passport: '',
      service_id: '',
      hotel_id: null,
      room_id: null,
      has_meals: false,
      computed_price: 0,
      service_label: '',
      trip_date: new Date().toISOString().split('T')[0],
      return_date: '',
      destination: '',
      duration_days: '',
      trip_details: '',
      supplier_name: '',
      supplier_contact: '',
      discount_amount: 0,
      commission_amount: 0,
      discount_reason: '',
      commission_reason: '',
      // 🆕 reset supplier fields
      service_origin: 'internal',
      supplier_id: '',
      supplier_cost: '',
      // 🆕 reset airline-discount fields
      discount_category: '',
      discount_document_file: null,
      // 🆕 reset age-based pricing (Phase 1)
      pricing_mode: 'fixed',
      age_pricing: {
        adult:                 { sell: '', cost: '' },
        teen:                  { sell: '', cost: '' },
        child:                 { sell: '', cost: '' },
        young_child_with_seat: { sell: '', cost: '' },
        young_child_no_seat:   { sell: 0,  cost: 0  },
        infant:                { sell: 0,  cost: 0  },
      },
      passengers: [],
    });
    setShowNewClientFields(true);
  };

  // Apply filters
  const applyFilters = () => {
    fetchTrips();
  };

  // View document function
  const viewDocument = (documentUrl, documentType, tripNo) => {
    if (!documentUrl) {
      alert('لا توجد وثيقة لعرضها');
      return;
    }

    const fullUrl = `${API}${documentUrl}`;
    
    if (documentType === 'pdf') {
      window.open(fullUrl, '_blank');
    } else {
      setModalImageUrl(fullUrl);
      setModalImageTitle(`وثيقة الرحلة ${tripNo}`);
      setShowDocumentModal(true);
    }
  };

  // View trip details
  const viewTripDetails = (trip) => {
    alert(`تفاصيل الرحلة ${trip.trip_no}:\n\nالعميل: ${trip.client_name}\nالخدمة: ${trip.service_name}\nالوجهة: ${trip.destination}\nالمورد: ${trip.supplier_name}\n\nالمبلغ النهائي: ${trip.final_amount?.toLocaleString()} دج\nالمدفوع: ${trip.total_paid?.toLocaleString()} دج\nالمتبقي: ${trip.remaining_amount?.toLocaleString()} دج`);
  };

  // Add payment to trip
  const addPayment = async () => {
    if (!paymentForm.amount || !paymentForm.document_file) {
      alert('يرجى إدخال مبلغ الدفعة ورفع وثيقة الوصل');
      return;
    }

    if (parseFloat(paymentForm.amount) <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    // 🛡️ Defensive guard — when the dialog is reopened from the
    // "إضافة دفعة جديدة" button inside the payments-details panel,
    // selectedTripForPayment used to be missing the `id` field which
    // caused a silent /undefined/payments 404.
    if (!selectedTripForPayment?.id) {
      alert('⚠️ تعذّر تحديد الرحلة. الرجاء إعادة فتح نافذة الدفعات والمحاولة مرة أخرى.');
      return;
    }

    if (parseFloat(paymentForm.amount) > selectedTripForPayment.remaining_amount) {
      alert('مبلغ الدفعة أكبر من المبلغ المتبقي');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // First, add the payment
      const paymentData = {
        trip_id: selectedTripForPayment.id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      };

      const response = await axios.post(`${API}/organized-trips/${selectedTripForPayment.id}/payments`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const paymentId = response.data.payment_id;
      
      // Upload document (Required)
      const formData = new FormData();
      formData.append('file', paymentForm.document_file);

      await axios.post(`${API}/trip-payments/${paymentId}/upload-document`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert(`✅ تم إضافة الدفعة بنجاح!\nرقم الوصل: ${response.data.payment_no}\nرقم العملية اليومية: ${response.data.operation_no}`);
      
      // Reset form and close dialog
      setShowPaymentDialog(false);
      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        notes: '',
        document_file: null
      });
      setDocumentPreview(null);
      setDocumentType('');

      // 🚀 Perf (2026-07-22): Optimistic in-place update so the user sees
      // the new totals immediately — no waiting for /organized-trips to
      // reload the whole list. Backend fetch happens in the background
      // to reconcile in case of drift.
      const _paidAdd = parseFloat(paymentForm.amount) || 0;
      const _tripId = selectedTripForPayment.id;
      setTrips(prev => prev.map(t => {
        if (t.id !== _tripId) return t;
        const newTotalPaid = (t.total_paid || 0) + _paidAdd;
        const newRemaining = (t.final_amount || 0) - newTotalPaid;
        let status = 'unpaid';
        if (newTotalPaid >= (t.final_amount || 0)) status = 'paid';
        else if (newTotalPaid > 0) status = 'partial';
        return {
          ...t,
          total_paid: newTotalPaid,
          remaining_amount: newRemaining,
          payments_count: (t.payments_count || 0) + 1,
          payment_status: status,
        };
      }));

      // Background reconcile (no await → doesn't block UI)
      fetchTrips();
      
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('❌ فشل في إضافة الدفعة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // NEW: Change trip service with financial calculations
  const changeService = async () => {
    if (!serviceChangeForm.new_service_id || !serviceChangeForm.reason.trim()) {
      alert('يرجى اختيار الخدمة الجديدة وإدخال سبب التغيير');
      return;
    }

    // 🆕 Hierarchical services require a computed price (room+meals)
    if (serviceChangeForm.service_kind === 'hierarchical'
        && (!serviceChangeForm.computed_price || serviceChangeForm.computed_price <= 0)) {
      alert('يرجى اختيار الفندق والغرفة لاحتساب السعر النهائي للخدمة الهرمية');
      return;
    }

    // 🆕 Mandatory proof document — backend will also enforce this.
    if (!serviceChangeForm.document) {
      alert('يرجى إرفاق وثيقة إثبات (صورة أو PDF، حد أقصى 5MB)');
      return;
    }
    const maxBytes = 5 * 1024 * 1024;
    if (serviceChangeForm.document.size > maxBytes) {
      alert('حجم الوثيقة يتجاوز 5MB');
      return;
    }
    const allowed = ['image/jpeg','image/png','image/webp','image/gif','application/pdf'];
    if (!allowed.includes(serviceChangeForm.document.type)) {
      alert('نوع الملف غير مدعوم. المسموح: صورة (JPG/PNG/WebP/GIF) أو PDF');
      return;
    }

    // 🆕 No native confirm() — it can get swallowed inside the preview iframe
    //    and the user sees the button as unresponsive. The dialog itself
    //    already shows the financial prediction and reason fields, so a
    //    second native confirm popup was redundant friction anyway.

    try {
      const token = localStorage.getItem('token');

      const fd = new FormData();
      fd.append('new_service_id', serviceChangeForm.new_service_id);
      fd.append('reason', serviceChangeForm.reason);
      if (serviceChangeForm.service_kind === 'hierarchical' && serviceChangeForm.computed_price > 0) {
        fd.append('computed_price', String(serviceChangeForm.computed_price));
      }
      fd.append('document', serviceChangeForm.document);
      fd.append('deduction_source', serviceChangeForm.deduction_source || 'cashbox');

      const response = await axios.put(
        `${API}/organized-trips/${selectedTripForServiceChange.id}/change-service`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data;

      // 🆕 Non-admin path → backend returns status='pending_approval' instead
      // of applying immediately. Surface a clear message and skip the
      // financial recap (no numbers have changed yet).
      if (result.status === 'pending_approval') {
        alert(
          '⏳ تم تسجيل طلبك بنجاح\n\n'
          + `الرحلة: ${selectedTripForServiceChange.trip_no}\n`
          + `الخدمة الجديدة: ${result.new_service}\n\n`
          + 'الطلب الآن بانتظار موافقة المدير العام أو المحاسب العام.\n'
          + '📎 الوثيقة محفوظة، ولن تتم أي حركة مالية قبل الاعتماد.'
        );
        setShowChangeServiceDialog(false);
        setServiceChangeForm({
          new_service_id: '',
          reason: '',
          service_kind: 'simple',
          computed_price: 0,
          new_service_label: '',
          hotel_id: null, room_id: null, has_meals: false,
          document: null,
          deduction_source: 'cashbox',
        });
        setServiceChangePrediction(null);
        fetchTrips();
        return;
      }
      
      // Show detailed result message
      let message = `✅ تم تغيير الخدمة بنجاح!\n\n`;
      message += `الخدمة السابقة: ${result.old_service}\n`;
      message += `الخدمة الجديدة: ${result.new_service}\n\n`;
      message += `📊 التفاصيل المالية:\n`;
      message += `المبلغ السابق: ${result.old_final_amount?.toLocaleString()} دج\n`;
      message += `المبلغ الجديد: ${result.new_final_amount?.toLocaleString()} دج\n`;
      message += `فرق السعر: ${result.price_difference > 0 ? '+' : ''}${result.price_difference?.toLocaleString()} دج\n\n`;
      message += `💰 حالة الدفع:\n`;
      message += `إجمالي المدفوع: ${result.total_paid?.toLocaleString()} دج\n`;
      message += `المتبقي الجديد: ${result.new_remaining_amount?.toLocaleString()} دج\n`;
      
      if (result.overpayment_amount > 0) {
        message += `\n💵 تعويض للعميل: ${result.overpayment_amount?.toLocaleString()} دج\n`;
        message += `✨ تم إنشاء عملية تعويض في العمليات اليومية`;
      } else if (result.new_remaining_amount > 0) {
        message += `\n💳 مطلوب دفع إضافي: ${result.new_remaining_amount?.toLocaleString()} دج`;
      }
      message += '\n📎 تم حفظ وثيقة الإثبات';

      alert(message);
      
      // Reset and close dialog
      setShowChangeServiceDialog(false);
      setServiceChangeForm({
        new_service_id: '',
        reason: '',
        service_kind: 'simple',
        computed_price: 0,
        new_service_label: '',
        hotel_id: null,
        room_id: null,
        has_meals: false,
        document: null,
        deduction_source: 'cashbox',
      });
      setServiceChangePrediction(null);
      
      // Refresh data
      fetchTrips();
      
    } catch (error) {
      console.error('Error changing service:', error);
      alert('❌ فشل في تغيير الخدمة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Calculate service change prediction
  const calculateServiceChangePrediction = () => {
    if (!selectedTripForServiceChange || !serviceChangeForm.new_service_id) {
      setServiceChangePrediction(null);
      return;
    }

    // 🆕 For hierarchical: use the room+meals computed price.
    //    For simple: look up the legacy service base_price.
    let newBasePrice;
    let newServiceName;
    if (serviceChangeForm.service_kind === 'hierarchical') {
      if (!serviceChangeForm.computed_price || serviceChangeForm.computed_price <= 0) {
        setServiceChangePrediction(null);
        return;
      }
      newBasePrice = serviceChangeForm.computed_price;
      newServiceName = serviceChangeForm.new_service_label
        || 'خدمة هرمية';
    } else {
      const newService = services.find(s => s.id === serviceChangeForm.new_service_id);
      if (!newService) {
        setServiceChangePrediction(null);
        return;
      }
      newBasePrice = newService.base_price;
      newServiceName = newService.name;
    }

    const oldBasePrice = selectedTripForServiceChange.base_price;
    const discountAmount = selectedTripForServiceChange.discount_amount || 0;
    const commissionAmount = selectedTripForServiceChange.commission_amount || 0;
    
    const oldFinalAmount = selectedTripForServiceChange.final_amount;
    const newFinalAmount = newBasePrice - discountAmount + commissionAmount;
    const priceDifference = newBasePrice - oldBasePrice;
    const totalPaid = selectedTripForServiceChange.total_paid || 0;
    const newRemainingAmount = newFinalAmount - totalPaid;
    
    setServiceChangePrediction({
      oldService: selectedTripForServiceChange.service_name,
      newService: newServiceName,
      oldBasePrice,
      newBasePrice,
      priceDifference,
      oldFinalAmount,
      newFinalAmount,
      totalPaid,
      newRemainingAmount,
      overpaymentAmount: newRemainingAmount < 0 ? Math.abs(newRemainingAmount) : 0,
      needsAdditionalPayment: newRemainingAmount > 0
    });
  };

  // Monitor service selection changes for prediction
  useEffect(() => {
    calculateServiceChangePrediction();
  }, [
    serviceChangeForm.new_service_id,
    serviceChangeForm.service_kind,
    serviceChangeForm.computed_price,
    selectedTripForServiceChange,
  ]);

  // 🆕 Fetch the linked flight's discount info when the user picks an umrah_trip
  useEffect(() => {
    const tid = tripForm.umrah_trip_id;
    if (!tid) { setLinkedFlightDiscount(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/flights/by-umrah-trip/${tid}/discount-info`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setLinkedFlightDiscount(res.data);
      } catch (e) {
        if (!cancelled) setLinkedFlightDiscount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [tripForm.umrah_trip_id]);

  // 🆕 Auto-fill trip discount when the user picks a discount category
  // (does NOT overwrite a manual value already entered)
  useEffect(() => {
    if (!tripForm.discount_category) return;
    if (!linkedFlightDiscount?.linked) return;
    const amt = Number(linkedFlightDiscount.discount_amount || 0);
    if (amt <= 0) return;
    const current = parseFloat(tripForm.discount_amount) || 0;
    if (current > 0) return; // don't overwrite user-entered value
    setTripForm(prev => ({
      ...prev,
      discount_amount: amt,
      discount_reason: prev.discount_reason ||
        `تخفيض شركة الطيران (${prev.discount_category}) — رحلة ${linkedFlightDiscount.flight_no || ''}`.trim(),
    }));
  }, [tripForm.discount_category, linkedFlightDiscount]);

  // Fetch trip payments details
  const fetchTripPayments = async (tripId) => {
    setPaymentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/organized-trips/${tripId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedTripPayments(response.data.trip_info);
      setTripPayments(response.data.payments);
      setShowPaymentDetailsDialog(true);
      
    } catch (error) {
      console.error('Error fetching trip payments:', error);
      alert('فشل في تحميل تفاصيل الدفعات');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Delete a single trip-payment installment. Backend now keeps the
  // parent daily_operation intact and just recomputes totals.
  // 🆕 Uses the unified confirm dialog instead of native confirm().
  const deletePayment = (payment) => {
    setConfirmDialog({
      kind: 'delete_payment',
      payment,
      title: `🗑️ حذف الدفعة ${payment.payment_no || ''}؟`,
      message: 'سيتم حذف الوصل والوثيقة المرفقة وإعادة احتساب رصيد الرحلة. العملية الأصلية للرحلة تبقى محفوظة. هذا الإجراء لا يمكن التراجع عنه.',
      askReason: false,
      confirmLabel: '🗑️ نعم، احذف',
      danger: true,
    });
    setConfirmReason('');
  };

  // Generate trips summary report
  const generateTripsReport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.trip_type && filters.trip_type !== 'all') {
        params.append('trip_type', filters.trip_type);
      }
      if (filters.agency_id && filters.agency_id !== 'all') {
        params.append('agency_id', filters.agency_id);
      }
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      if (filters.supplier_name) {
        params.append('supplier_name', filters.supplier_name);
      }
      if (filters.service_id && filters.service_id !== 'all') {
        params.append('service_id', filters.service_id);
      }
      
      const response = await axios.get(`${API}/organized-trips/reports/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTripsReportData(response.data);
      setShowTripsReportDialog(true);
      
    } catch (error) {
      console.error('Error generating trips report:', error);
      alert('فشل في إنشاء تقرير الرحلات');
    }
  };

  // Generate adjustments report
  const generateAdjustmentsReport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.trip_type && filters.trip_type !== 'all') {
        params.append('trip_type', filters.trip_type);
      }
      if (filters.agency_id && filters.agency_id !== 'all') {
        params.append('agency_id', filters.agency_id);
      }
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      
      const response = await axios.get(`${API}/organized-trips/reports/adjustments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAdjustmentsReportData(response.data);
      setShowAdjustmentsReportDialog(true);
      
    } catch (error) {
      console.error('Error generating adjustments report:', error);
      alert('فشل في إنشاء تقرير التخفيضات والعمولات');
    }
  };

  // ============================================================
  // 🆕 Trip approval flow — mirrors the ticket flow.
  // Only general_accountant / super_admin can approve or reject,
  // and never their own creations. Approve flips the trip + mirror
  // daily_operation to APPROVED; reject deletes the mirror and
  // returns the trip to the staff with a reason.
  // ============================================================
  const canApprove = user?.role === 'general_accountant' || user?.role === 'super_admin';

  const approveTrip = (trip) => {
    if (!canApprove) return;
    if (trip.created_by === user?.id) {
      alert('⛔ لا يمكنك اعتماد رحلة أنشأتها بنفسك. يجب أن يعتمدها شخص آخر.');
      return;
    }
    // 🤝 Subagent flow — open the supplier-picker dialog instead.
    const agency = agencies.find(a => a.id === trip.agency_id);
    if (agency?.is_subagent) {
      setSubagentApprovalTarget({
        trip,
        itemLabel: `رحلة ${trip.trip_no || ''}`,
        agencyName: agency?.name,
        sellPrice: trip.final_price || trip.base_price,
      });
      return;
    }
    setConfirmDialog({
      kind: 'approve_trip',
      trip,
      title: `✅ اعتماد الرحلة ${trip.trip_no || ''}؟`,
      message: `سيصبح المبلغ الإجمالي للرحلة مرئياً في تقارير العمليات اليومية. الدفعات الفردية تظل تحتاج اعتماداً منفصلاً.`,
      askReason: false,
      confirmLabel: '✅ نعم، اعتمد',
      danger: false,
    });
    setConfirmReason('');
  };

  // 🤝 Subagent approval dialog state + handler
  const [subagentApprovalTarget, setSubagentApprovalTarget] = useState(null);
  const [subagentApprovalBusy, setSubagentApprovalBusy] = useState(false);
  const handleSubagentApprovalConfirm = async (payload) => {
    if (!subagentApprovalTarget) return;
    setSubagentApprovalBusy(true);
    try {
      await axios.post(
        `${API}/organized-trips/${subagentApprovalTarget.trip.id}/approve`,
        payload,
        { headers: getAuthHeaders() },
      );
      setSubagentApprovalTarget(null);
      fetchTrips();
    } catch (e) {
      alert('❌ فشل الاعتماد: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubagentApprovalBusy(false);
    }
  };

  const rejectTrip = (trip) => {
    if (!canApprove) return;
    if (trip.created_by === user?.id) {
      alert('⛔ لا يمكنك رفض رحلة أنشأتها بنفسك.');
      return;
    }
    setConfirmDialog({
      kind: 'reject_trip',
      trip,
      title: `❌ رفض الرحلة ${trip.trip_no || ''}؟`,
      message: 'الرحلة ستعود للموظف بحالة "مرفوضة" مع السبب — يمكنه إصلاحها وإعادة الإرسال.',
      askReason: true,
      confirmLabel: '❌ نعم، ارفض',
      danger: true,
    });
    setConfirmReason('');
  };

  // 🆕 Per-installment approval: each new trip_payment by a staff user
  // requires its own approval before its amount is rolled into the
  // parent daily_op total_paid (and thus into the agency balance).
  const approveTripPayment = (payment) => {
    if (!canApprove) return;
    if (payment.recorded_by === user?.id) {
      alert('⛔ لا يمكنك اعتماد دفعة سجّلتها بنفسك. يجب أن يعتمدها شخص آخر.');
      return;
    }
    setConfirmDialog({
      kind: 'approve_payment',
      payment,
      title: `✅ اعتماد الدفعة ${payment.payment_no || ''}؟`,
      message: `المبلغ: ${(payment.amount || 0).toLocaleString()} دج. بعد الاعتماد، سيُضاف هذا المبلغ إلى رصيد الوكالة في الحالة المالية.`,
      askReason: false,
      confirmLabel: '✅ نعم، اعتمد الدفعة',
      danger: false,
    });
    setConfirmReason('');
  };

  const rejectTripPayment = (payment) => {
    if (!canApprove) return;
    if (payment.recorded_by === user?.id) {
      alert('⛔ لا يمكنك رفض دفعة سجّلتها بنفسك.');
      return;
    }
    setConfirmDialog({
      kind: 'reject_payment',
      payment,
      title: `❌ رفض الدفعة ${payment.payment_no || ''}؟`,
      message: 'الدفعة ستُحفظ بحالة "مرفوضة" مع السبب — الموظف سيراها ويمكنه التصحيح.',
      askReason: true,
      confirmLabel: '❌ نعم، ارفض الدفعة',
      danger: true,
    });
    setConfirmReason('');
  };

  // 🆕 Single executor for the unified confirm dialog. Runs only when
  // the user clicks the inner "نعم" button — never on page load and
  // never from a chain of native popups, so it survives Chrome's
  // popup-suppression heuristics.
  const executeConfirm = async () => {
    if (!confirmDialog) return;
    const { kind, payment, trip, askReason } = confirmDialog;
    if (askReason && confirmReason.trim().length < 3) {
      alert('⚠️ يرجى كتابة سبب الرفض (3 أحرف على الأقل)');
      return;
    }
    setConfirmBusy(true);
    try {
      const headers = getAuthHeaders();
      if (kind === 'approve_payment') {
        await axios.post(`${API}/trip-payments/${payment.id}/approve`, {}, { headers });
        if (payment.trip_id) fetchTripPayments(payment.trip_id);
        fetchTrips();
      } else if (kind === 'reject_payment') {
        const fd = new FormData();
        fd.append('reason', confirmReason.trim());
        await axios.post(`${API}/trip-payments/${payment.id}/reject`, fd, { headers });
        if (payment.trip_id) fetchTripPayments(payment.trip_id);
        fetchTrips();
      } else if (kind === 'approve_trip') {
        await axios.post(`${API}/organized-trips/${trip.id}/approve`, {}, { headers });
        fetchTrips();
      } else if (kind === 'reject_trip') {
        const fd = new FormData();
        fd.append('reason', confirmReason.trim());
        await axios.post(`${API}/organized-trips/${trip.id}/reject`, fd, { headers });
        fetchTrips();
      } else if (kind === 'delete_payment') {
        const r = await axios.delete(`${API}/trip-payments/${payment.id}`, { headers });
        const result = r.data || {};
        if (payment.trip_id) fetchTripPayments(payment.trip_id);
        fetchTrips();
        if (result.new_total_paid !== undefined) {
          alert(`✅ تم حذف الدفعة بنجاح\nالمبلغ المحذوف: ${result.deleted_amount?.toLocaleString?.()} دج`);
        }
      } else if (kind === 'delete_trip') {
        const r = await axios.delete(`${API}/organized-trips/${trip.id}`, { headers });
        fetchTrips();
        const result = r.data || {};
        alert(
          `✅ تم حذف الرحلة وكل آثارها\n` +
          `• العملية اليومية: ${result.daily_operation_deleted ? 'تم' : '—'}\n` +
          `• الدفعات: ${result.payments_deleted || 0}\n` +
          `• دفعات الرحلة: ${result.trip_payments_deleted || 0}\n` +
          `• تعديلات الرحلة: ${result.trip_adjustments_deleted || 0}\n` +
          `• قيود التتبع المالي: ${result.financial_entries_deleted || 0}`
        );
      }
      setConfirmDialog(null);
      setConfirmReason('');
    } catch (e) {
      alert('❌ فشلت العملية: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setConfirmBusy(false);
    }
  };

  // Initialize data
  useEffect(() => {
    fetchTrips();
    fetchServices();
    fetchClients();
    
    // Fetch travel groups (Umrah/Hajj/Turkey/Tourism) for linking
    axios.get(`${API}/umrah/dashboard/stats`, { headers: getAuthHeaders() })
      .then(r => setUmrahTrips(r.data || []))
      .catch(() => {});
    
    if (user?.role === 'general_accountant' || user?.role === 'super_admin') {
      fetchAgencies();
    }
    // 🆕 Load active suppliers for the service-origin picker
    (async () => {
      try {
        const r = await axios.get(`${API}/suppliers?status=active`, { headers: getAuthHeaders() });
        setSuppliers(r.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🗺️ إدارة الرحلات المنظمة</h1>
          <p className="text-sm text-gray-600 mt-1">إدارة شاملة للرحلات المنظمة مع نظام الدفعات المتقطعة</p>
        </div>
        <Button onClick={() => setShowAddTripDialog(true)} className="bg-green-600 hover:bg-green-700">
          ➕ إضافة رحلة منظمة جديدة
        </Button>
      </div>

      {/* Filters Section */}
      {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
        <Card>
          <CardHeader>
            <CardTitle>🔍 فلاتر البحث والتصفية</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {/* Trip Type Filter */}
              <div>
                <Label>نوع الرحلة</Label>
                <select
                  value={filters.trip_type}
                  onChange={(e) => setFilters({...filters, trip_type: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="all">جميع الأنواع</option>
                  <option value="عمرة">🕋 عمرة</option>
                  <option value="خارجية">🌍 خارجية</option>
                  <option value="داخلية">🏠 داخلية</option>
                </select>
              </div>
              
              {/* Agency Filter */}
              <div>
                <Label>الوكالة</Label>
                <select
                  value={filters.agency_id}
                  onChange={(e) => setFilters({...filters, agency_id: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="all">جميع الوكالات</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name} - {agency.city}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Date Filters */}
              <div>
                <Label>من تاريخ</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>إلى تاريخ</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>
              
              {/* Supplier Filter */}
              <div>
                <Label>المورد الخارجي</Label>
                <Input
                  value={filters.supplier_name}
                  onChange={(e) => setFilters({...filters, supplier_name: e.target.value})}
                  placeholder="اسم المورد"
                />
              </div>
              
              {/* Service Filter (includes hierarchical services with search) */}
              <div>
                <Label>الخدمة</Label>
                <SearchableSelect
                  value={filters.service_id === 'all' ? '' : filters.service_id}
                  onChange={(v) => setFilters({...filters, service_id: v || 'all'})}
                  options={[
                    ...services.map(s => ({
                      value: s.id,
                      label: s.name,
                      hint: 'خدمة عادية',
                      search: `${s.name} ${s.description || ''}`,
                    })),
                    ...hierarchicalServices.map(s => ({
                      value: s.id,
                      label: `🗂️ ${s.name}`,
                      hint: s.departure_date || 'خدمة هرمية',
                      search: `${s.name} ${s.departure_date || ''} ${s.description || ''}`,
                    })),
                  ]}
                  placeholder="جميع الخدمات — ابحث..."
                  searchPlaceholder="🔎 ابحث باسم الخدمة (عادية أو هرمية)..."
                  emptyText="لا توجد خدمة مطابقة"
                  testId="trips-filter-service"
                />
              </div>

              {/* 🆕 Payment Status Filter */}
              <div>
                <Label>حالة الدفع</Label>
                <select
                  value={filters.payment_status}
                  onChange={(e) => setFilters({...filters, payment_status: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  data-testid="trips-filter-payment-status"
                >
                  <option value="all">🔄 كل الحالات</option>
                  <option value="paid">✅ مدفوع كاملاً</option>
                  <option value="partial">🟡 جزئي</option>
                  <option value="unpaid">❌ غير مدفوع</option>
                  <option value="exceptional_discount">💸 مطبق عليه تخفيض استثنائي</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-4 space-x-2">
              <Button onClick={applyFilters} className="bg-brand-600 hover:bg-brand-700">
                🔍 تطبيق الفلاتر
              </Button>
              <Button onClick={generateTripsReport} className="bg-green-600 hover:bg-green-700">
                📊 تقرير الرحلات
              </Button>
              <Button onClick={generateAdjustmentsReport} className="bg-purple-600 hover:bg-purple-700">
                📈 تقرير التخفيضات والعمولات
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trips Table */}
      <Card>
        <CardHeader>
          <CardTitle>📋 جدول الرحلات المنظمة</CardTitle>
          <CardDescription>
            جميع الرحلات المنظمة مع تتبع الدفعات والمبالغ
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 🆕 Pending-mode quick filter — auto-on when arriving from the
              dashboard chip, freely toggleable afterwards. */}
          {(() => {
            const pendingTripsCount = trips.filter(t => t.status === 'pending_approval').length;
            const visibleCount = pendingMode === 'all'
              ? trips.length
              : pendingMode === 'pending_trips'
                ? pendingTripsCount
                : (tripsWithPendingPayments ? trips.filter(t => tripsWithPendingPayments.has(t.id)).length : 0);
            if (trips.length === 0) return null;
            return (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-2.5 rounded-lg border bg-amber-50/40">
                <div className="text-sm font-semibold text-gray-700">
                  {pendingMode === 'all' && `📋 عرض كل الرحلات (${trips.length})${pendingTripsCount > 0 ? ` — ${pendingTripsCount} بانتظار الاعتماد` : ''}`}
                  {pendingMode === 'pending_trips' && `🟡 الرحلات بانتظار الاعتماد فقط (${visibleCount}/${trips.length})`}
                  {pendingMode === 'pending_payments' && `🔴 الرحلات التي لديها دفعات بانتظار الاعتماد (${visibleCount}/${trips.length})`}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setPendingMode('all')}
                    className={
                      'px-3 py-1.5 rounded text-xs font-bold transition ' +
                      (pendingMode === 'all' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                    }
                    data-testid="trips-mode-all"
                  >
                    🔄 الكل
                  </button>
                  <button
                    onClick={() => setPendingMode('pending_trips')}
                    className={
                      'px-3 py-1.5 rounded text-xs font-bold transition ' +
                      (pendingMode === 'pending_trips' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200')
                    }
                    data-testid="trips-mode-pending-trips"
                  >
                    🟡 رحلات معلَّقة
                  </button>
                  <button
                    onClick={() => setPendingMode('pending_payments')}
                    className={
                      'px-3 py-1.5 rounded text-xs font-bold transition ' +
                      (pendingMode === 'pending_payments' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-200')
                    }
                    data-testid="trips-mode-pending-payments"
                  >
                    🔴 دفعات معلَّقة
                  </button>
                </div>
              </div>
            );
          })()}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">جاري تحميل الرحلات...</p>
            </div>
          ) : trips.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>لا توجد رحلات منظمة مسجلة حالياً</p>
              <p className="text-sm">اضغط "إضافة رحلة منظمة جديدة" لبدء التسجيل</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">👤 الموظف</TableHead>
                    <TableHead className="text-right">رقم الرحلة</TableHead>
                    <TableHead className="text-right">نوع الرحلة</TableHead>
                    <TableHead className="text-right">اسم العميل</TableHead>
                    <TableHead className="text-right">👶 الأعمار</TableHead>
                    <TableHead className="text-right">الخدمة</TableHead>
                    <TableHead className="text-right">🛏️ نوع الغرفة</TableHead>
                    <TableHead className="text-right">الوجهة</TableHead>
                    <TableHead className="text-right">تاريخ الرحلة</TableHead>
                    <TableHead className="text-right">المورد الخارجي</TableHead>
                    <TableHead className="text-right">💰 تكلفة المورد</TableHead>
                    <TableHead className="text-right">📈 الربح</TableHead>
                    <TableHead className="text-right">المبلغ النهائي (دج)</TableHead>
                    <TableHead className="text-right">المدفوع (دج)</TableHead>
                    <TableHead className="text-right">المتبقي (دج)</TableHead>
                    <TableHead className="text-right">حالة الدفع</TableHead>
                    <TableHead className="text-right">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    let visibleTrips = trips;
                    if (pendingMode === 'pending_trips') {
                      visibleTrips = trips.filter(t => t.status === 'pending_approval');
                    } else if (pendingMode === 'pending_payments' && tripsWithPendingPayments) {
                      visibleTrips = trips.filter(t => tripsWithPendingPayments.has(t.id));
                    }
                    // 🆕 Payment status filter (client-side): paid / partial / unpaid /
                    // exceptional_discount (credit_balance > 0 OR last_credit_note_at set).
                    if (filters.payment_status && filters.payment_status !== 'all') {
                      visibleTrips = visibleTrips.filter(t => {
                        if (filters.payment_status === 'exceptional_discount') {
                          return (t.credit_balance || 0) > 0 || !!t.last_credit_note_at;
                        }
                        if (filters.payment_status === 'paid') return t.payment_status === 'paid';
                        if (filters.payment_status === 'partial') return t.payment_status === 'partial';
                        if (filters.payment_status === 'unpaid') {
                          return t.payment_status !== 'paid' && t.payment_status !== 'partial';
                        }
                        return true;
                      });
                    }
                    return visibleTrips.map((trip) => (
                    <TableRow key={trip.id}>
                      {/* 👤 Employee who created the trip */}
                      <TableCell className="text-right text-sm font-medium text-amber-700">
                        {trip.created_by_name || '—'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-brand-600">
                        {trip.trip_no}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          trip.trip_type === 'عمرة' ? 'bg-green-100 text-green-800' :
                          trip.trip_type === 'خارجية' ? 'bg-brand-100 text-brand-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {trip.trip_type === 'عمرة' ? '🕋' : trip.trip_type === 'خارجية' ? '🌍' : '🏠'} {trip.trip_type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {trip.client_name}
                      </TableCell>
                      {/* 👶 Passenger ages summary (age-based trips only) */}
                      <TableCell className="text-right" data-testid={`trip-row-ages-${trip.id}`}>
                        {trip.pricing_mode === 'age_based' && Array.isArray(trip.passengers) && trip.passengers.length > 0 ? (
                          <div
                            className="text-xs text-gray-800 leading-relaxed"
                            title={trip.passengers.map(p => `${p.name || '—'}: ${p.age ?? '—'} سنة`).join('\n')}
                          >
                            {trip.passengers.map((p, i) => (
                              <span key={p.id || i} className="inline-block whitespace-nowrap mx-0.5">
                                <span className="font-bold text-brand-700">{p.age ?? '—'}</span>
                                <span className="text-gray-500 mr-0.5">سنة</span>
                                {i < trip.passengers.length - 1 && <span className="text-gray-300 mx-0.5">·</span>}
                              </span>
                            ))}
                            <div className="text-[10px] text-gray-400 mt-0.5">إجمالي: {trip.passengers.length} مسافر</div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {trip.service_name}
                      </TableCell>
                      <TableCell className="text-right">
                        {trip.room_type ? (
                          <span
                            className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 border border-amber-200 text-amber-800"
                            data-testid={`trip-roomtype-${trip.id}`}
                            title={trip.hotel_name ? `الفندق: ${trip.hotel_name}` : ''}
                          >
                            🛏️ {trip.room_type}{trip.has_meals ? ' • مع إعاشة' : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {trip.destination}
                      </TableCell>
                      <TableCell className="text-right">
                        <div>{new Date(trip.trip_date).toLocaleDateString('ar-DZ')}</div>
                        {trip.created_at && (
                          <div className="text-xs text-gray-500" dir="ltr">
                            🕐 {formatTimeWithEnglishNumerals(trip.created_at)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {trip.supplier_name}
                      </TableCell>

                      {/* 🆕 Supplier Cost (transparency mode) */}
                      <TableCell className="text-right" data-testid={`trip-row-cost-${trip.id}`}>
                        {trip.supplier_cost != null && trip.supplier_cost > 0 ? (
                          <span className="text-red-700 font-semibold">{Number(trip.supplier_cost).toLocaleString()} دج</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right" data-testid={`trip-row-profit-${trip.id}`}>
                        {trip.supplier_cost != null && trip.supplier_cost > 0 ? (
                          (() => {
                            const profit = (trip.final_amount || 0) - trip.supplier_cost;
                            return (
                              <span className={profit >= 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
                                {profit >= 0 ? '+' : ''}{Number(profit).toLocaleString()} دج
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </TableCell>

                      <TableCell className="text-right font-bold text-brand-600">
                        {trip.final_amount?.toLocaleString()} دج
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        {trip.total_paid?.toLocaleString()} دج
                      </TableCell>
                      <TableCell className="text-right font-bold text-orange-600">
                        {trip.remaining_amount?.toLocaleString()} دج
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          trip.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                          trip.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {trip.payment_status === 'paid' ? '✅ مدفوع كاملاً' :
                           trip.payment_status === 'partial' ? '🟡 جزئي' : '❌ غير مدفوع'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1 flex-wrap">
                          {/* 🆕 Approval status indicators */}
                          {trip.status === 'pending_approval' && (
                            <span className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full font-bold border border-amber-300 whitespace-nowrap" data-testid={`badge-pending-trip-${trip.id}`}>
                              ⏳ بانتظار الاعتماد
                            </span>
                          )}
                          {trip.status === 'rejected' && (
                            <span
                              className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-bold border border-red-300 whitespace-nowrap cursor-help"
                              title={trip.rejection_reason ? `سبب الرفض: ${trip.rejection_reason}` : 'مرفوضة'}
                              data-testid={`badge-rejected-trip-${trip.id}`}
                            >
                              ❌ مرفوضة
                            </span>
                          )}

                          {/* 🆕 Approve / Reject buttons */}
                          {canApprove && trip.status === 'pending_approval' && trip.created_by !== user?.id && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => approveTrip(trip)}
                                className="text-green-700 border-green-400 hover:bg-green-50 font-bold"
                                data-testid={`approve-trip-${trip.id}`}
                              >
                                ✅ اعتماد
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => rejectTrip(trip)}
                                className="text-red-700 border-red-400 hover:bg-red-50 font-bold"
                                data-testid={`reject-trip-${trip.id}`}
                              >
                                ❌ رفض
                              </Button>
                            </>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedTripForPayment(trip);
                              setShowPaymentDialog(true);
                            }}
                            className="text-green-600 hover:text-green-700"
                          >
                            💰 دفعة
                          </Button>

                          {/* 🆕 2026-07-22: Convert remaining balance to installment plan.
                              Only shows when there's remaining and the trip is not already
                              converted. Mirrors the Daily Operations workflow. */}
                          {(!trip.installment_receipt_id
                            && (trip.remaining_amount || 0) > 0
                            && trip.payment_status !== 'paid'
                            && trip.status !== 'pending_approval') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openConvertDialog(trip)}
                              className="text-purple-700 border-purple-300 hover:bg-purple-50"
                              title="تحويل المبلغ المتبقي إلى نظام الأقساط"
                              data-testid={`trip-convert-to-installments-${trip.id}`}
                            >
                              💳 تقسيط
                            </Button>
                          )}

                          {trip.installment_receipt_id && (
                            <span
                              className="text-xs px-2 py-1 rounded-md bg-purple-100 text-purple-800 border border-purple-300 font-bold self-center"
                              title={`رقم وصل التقسيط: ${trip.installment_receipt_no || ''}`}
                              data-testid={`trip-under-installments-${trip.id}`}
                            >
                              💳 تحت الأقساط
                            </span>
                          )}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchTripPayments(trip.id)}
                            className="text-purple-600 hover:text-purple-700"
                            title="عرض جميع الدفعات مع الوثائق"
                          >
                            📋 الدفعات
                          </Button>
                          
                          {/* 🆕 «تغيير الخدمة» — مفتوح للجميع، non-admins ينشئون طلباً معلّقاً */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedTripForServiceChange(trip);
                              setServiceChangeForm({
                                new_service_id: '',
                                reason: '',
                                service_kind: 'simple',
                                computed_price: 0,
                                new_service_label: '',
                                hotel_id: null, room_id: null, has_meals: false,
                                document: null,
                                deduction_source: 'cashbox',
                              });
                              setServiceChangePrediction(null);
                              setShowChangeServiceDialog(true);
                            }}
                            className="text-orange-600 hover:text-orange-700"
                            title="تغيير خدمة الرحلة مع حساب الفروقات"
                          >
                            🔄 تغيير الخدمة
                          </Button>

                          {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => viewTripDetails(trip)}
                                className="text-brand-600 hover:text-brand-700"
                              >
                                👁️ تفاصيل
                              </Button>

                              {/* 🆕 Delete trip — cascades to daily_op +
                                  payments + supplier ledger + financial
                                  entries (refused if cash already
                                  confirmed in a transfer batch). */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setConfirmDialog({
                                    kind: 'delete_trip',
                                    trip,
                                    title: `🗑️ حذف الرحلة ${trip.trip_no || ''}؟`,
                                    message: 'سيتم حذف الرحلة + العملية اليومية المرتبطة + كل الدفعات والتعديلات + قيد دفتر المورد + قيد التتبع المالي. هذا الإجراء لا يمكن التراجع عنه — وسيُرفض إذا كانت الدفعة محوّلة إلى تحويل نقدي مغلق.',
                                    askReason: false,
                                    confirmLabel: '🗑️ نعم، احذف الرحلة',
                                    danger: true,
                                  });
                                  setConfirmReason('');
                                }}
                                className="text-red-700 border-red-400 hover:bg-red-50 font-bold"
                                data-testid={`delete-trip-${trip.id}`}
                                title="حذف الرحلة وكل آثارها"
                              >
                                🗑️ حذف
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ));
                  })()}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Add Trip Dialog - Complete Implementation */}
      <Dialog open={showAddTripDialog} onOpenChange={setShowAddTripDialog}>
        <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>➕ إضافة رحلة منظمة جديدة</DialogTitle>
            <DialogDescription>
              إنشاء رحلة منظمة جديدة مع نظام الدفعات المتقطعة والتخفيضات/العمولات
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Main Form - Horizontal Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Column 1: Trip Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-brand-800 border-b pb-1">🗺️ معلومات الرحلة</h3>
                
                <div>
                  <Label>نوع الرحلة *</Label>
                  <select
                    value={tripForm.trip_type}
                    onChange={(e) => setTripForm({...tripForm, trip_type: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="عمرة">🕋 عمرة</option>
                    <option value="خارجية">🌍 رحلة خارجية</option>
                    <option value="داخلية">🏠 رحلة داخلية</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <CascadingServiceSelector
                    value={{
                      service_id: tripForm.service_id,
                      hotel_id: tripForm.hotel_id,
                      room_id: tripForm.room_id,
                      has_meals: tripForm.has_meals
                    }}
                    onChange={async (v) => {
                      // 🆕 Auto-load age_pricing preset when the service changes
                      let ap = tripForm.age_pricing;
                      let hadPreset = false;
                      if (v.service_id && v.service_id !== tripForm.service_id) {
                        const preset = await fetchServiceAgePricing(v.service_id);
                        if (preset) {
                          hadPreset = true;
                          // Merge preset into form (keep the same shape used by the form)
                          ap = {
                            adult:                 { sell: preset.adult?.sell ?? '',                 cost: preset.adult?.cost ?? '' },
                            teen:                  { sell: preset.teen?.sell ?? '',                  cost: preset.teen?.cost ?? '' },
                            child:                 { sell: preset.child?.sell ?? '',                 cost: preset.child?.cost ?? '' },
                            young_child_with_seat: { sell: preset.young_child_with_seat?.sell ?? '', cost: preset.young_child_with_seat?.cost ?? '' },
                            young_child_no_seat:   { sell: preset.young_child_no_seat?.sell ?? 0,    cost: preset.young_child_no_seat?.cost ?? 0 },
                            infant:                { sell: preset.infant?.sell ?? 0,                 cost: preset.infant?.cost ?? 0 },
                          };
                        }
                      }
                      setTripForm({
                        ...tripForm,
                        service_id: v.service_id || '',
                        hotel_id: v.hotel_id || null,
                        room_id: v.room_id || null,
                        has_meals: !!v.has_meals,
                        computed_price: v.total_price || 0,
                        service_label: v.service_label || '',
                        age_pricing: ap,
                        // Auto-switch to age_based mode when the service ships a preset
                        pricing_mode: hadPreset ? 'age_based' : tripForm.pricing_mode,
                      });
                    }}
                    required
                  />
                </div>

                {/* Link to Travel Group (Umrah/Hajj/Turkey...) */}
                <div className="md:col-span-2">
                  <Label>🛫 ربط بفوج (اختياري — لتجميع الحجوزات)</Label>
                  <SearchableSelect
                    value={tripForm.umrah_trip_id || ''}
                    onChange={(selectedId) => {
                      const selectedTrip = umrahTrips.find(t => t.id === selectedId);
                      setTripForm({
                        ...tripForm,
                        umrah_trip_id: selectedId,
                        // Auto-populate departure & return dates from the selected group
                        ...(selectedTrip ? {
                          trip_date: selectedTrip.departure_date || tripForm.trip_date,
                          return_date: selectedTrip.return_date || tripForm.return_date,
                        } : {})
                      });
                    }}
                    options={umrahTrips.filter(t => !t.is_full).map(t => ({
                      value: t.id,
                      label: `[${t.trip_type}] ${t.name}`,
                      hint: `${t.remaining_seats} مكان متبقي${t.departure_date ? ` • ${t.departure_date}` : ''}`,
                      search: `${t.trip_type} ${t.name} ${t.departure_date || ''} ${t.return_date || ''}`,
                    }))}
                    placeholder="— بدون فوج —"
                    searchPlaceholder="🔎 ابحث باسم الفوج أو النوع..."
                    emptyText="لا يوجد فوج مطابق"
                    testId="umrah-trip-link-select"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 اربط هذا الحجز بفوج لتظهر الحجوزات مجمّعة في "إدارة الأفواج"
                  </p>
                </div>

                <div>
                  <Label>الوجهة *</Label>
                  <Input
                    value={tripForm.destination}
                    onChange={(e) => setTripForm({...tripForm, destination: e.target.value})}
                    placeholder="وجهة الرحلة"
                  />
                </div>

                <div>
                  <Label>تاريخ الرحلة *</Label>
                  <Input
                    type="date"
                    value={tripForm.trip_date}
                    onChange={(e) => setTripForm({...tripForm, trip_date: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>تاريخ العودة</Label>
                  <Input
                    type="date"
                    value={tripForm.return_date}
                    onChange={(e) => setTripForm({...tripForm, return_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>مدة الرحلة (بالأيام)</Label>
                  <Input
                    type="number"
                    value={tripForm.duration_days}
                    onChange={(e) => setTripForm({...tripForm, duration_days: e.target.value})}
                    placeholder="عدد الأيام"
                  />
                </div>

                <div>
                  <Label>تفاصيل الرحلة</Label>
                  <Textarea
                    value={tripForm.trip_details}
                    onChange={(e) => setTripForm({...tripForm, trip_details: e.target.value})}
                    placeholder="تفاصيل إضافية عن الرحلة"
                    rows={3}
                  />
                </div>

                {/* 🆕 Age-based pricing (Phase 1) — for family / bus trips
                    where children pay different prices based on age. */}
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-800 mb-3">🧾 نظام التسعير</h4>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3" data-testid="trip-pricing-mode-block">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setTripForm({ ...tripForm, pricing_mode: 'fixed' })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${tripForm.pricing_mode === 'fixed' ? 'bg-brand-100 border-brand-400 text-brand-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                        data-testid="pricing-mode-fixed-btn"
                      >💵 سعر ثابت (خدمة هرمية)</button>
                      <button
                        type="button"
                        onClick={() => setTripForm({ ...tripForm, pricing_mode: 'age_based' })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${tripForm.pricing_mode === 'age_based' ? 'bg-emerald-100 border-emerald-500 text-emerald-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                        data-testid="pricing-mode-age-based-btn"
                      >👨‍👩‍👧‍👦 حسب الأعمار (للعائلات)</button>
                    </div>

                    {tripForm.pricing_mode === 'age_based' && (
                      <div className="space-y-3 pt-2" data-testid="age-pricing-panel">
                        {/* Info banner for staff (prices come from the manager) */}
                        {!canEditAgePricing && (
                          <div className="text-[11px] bg-blue-50 border border-blue-200 rounded-md p-2 text-blue-800">
                            ℹ️ الأسعار مضبوطة مسبقاً من قِبَل المدير/المحاسب على مستوى الخدمة. أنت فقط تدخل اسم وعمر كل مسافر وسيتم تطبيق السعر تلقائياً.
                          </div>
                        )}
                        {/* Tier price table */}
                        <div>
                          <Label className="text-xs font-semibold text-gray-700">
                            أسعار الشرائح العمرية
                            {!canEditAgePricing && <span className="text-[10px] text-gray-500 mr-2">(للعرض فقط)</span>}
                          </Label>
                          <div className="mt-2 overflow-x-auto">
                            <table className="w-full text-xs border border-gray-200 rounded-md" data-testid="age-pricing-table">
                              <thead className="bg-gray-100 text-gray-700">
                                <tr>
                                  <th className="text-right px-2 py-1">الشريحة</th>
                                  <th className="text-right px-2 py-1">سعر البيع (دج)</th>
                                  {tripForm.service_origin === 'supplier' && (
                                    <th className="text-right px-2 py-1">تكلفة المورد (دج)</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {['adult','teen','child','young_child_with_seat','young_child_no_seat','infant'].map(tier => (
                                  <tr key={tier} className="border-t border-gray-200">
                                    <td className="px-2 py-1 text-gray-700 whitespace-nowrap">
                                      {AGE_TIER_META[tier].label}
                                      <div className="text-[10px] text-gray-400">{AGE_TIER_META[tier].range}</div>
                                    </td>
                                    <td className="px-2 py-1">
                                      <Input
                                        type="number"
                                        value={tripForm.age_pricing[tier].sell}
                                        onChange={e => setTripForm({
                                          ...tripForm,
                                          age_pricing: { ...tripForm.age_pricing, [tier]: { ...tripForm.age_pricing[tier], sell: e.target.value } }
                                        })}
                                        placeholder={tier === 'infant' || tier === 'young_child_no_seat' ? '0 (مجاناً)' : 'السعر'}
                                        className="h-7 text-xs"
                                        readOnly={!canEditAgePricing}
                                        disabled={!canEditAgePricing}
                                        data-testid={`age-price-${tier}-sell`}
                                      />
                                    </td>
                                    {tripForm.service_origin === 'supplier' && (
                                      <td className="px-2 py-1">
                                        <Input
                                          type="number"
                                          value={tripForm.age_pricing[tier].cost}
                                          onChange={e => setTripForm({
                                            ...tripForm,
                                            age_pricing: { ...tripForm.age_pricing, [tier]: { ...tripForm.age_pricing[tier], cost: e.target.value } }
                                          })}
                                          placeholder={tier === 'infant' || tier === 'young_child_no_seat' ? '0' : 'التكلفة'}
                                          className="h-7 text-xs"
                                          readOnly={!canEditAgePricing}
                                          disabled={!canEditAgePricing}
                                          data-testid={`age-price-${tier}-cost`}
                                        />
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* 🆕 (2026-07) Age-tier count grid — replaces the
                             per-passenger list. The operator just types the
                             head-count per tier; synthetic passengers are
                             created behind the scenes so the backend contract
                             stays untouched. Individual customer info lives
                             on the client record. */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-semibold text-gray-700">
                              👥 عدد الأشخاص لكل شريحة عمرية
                            </Label>
                            <span className="text-[11px] text-gray-500">
                              الإجمالي: <strong className="text-brand-700" data-testid="age-total-pax">{totalAgeBasedPax()}</strong> شخص
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="age-tier-counts-grid">
                            {AGE_TIER_ORDER.map((tier) => {
                              const meta = AGE_TIER_META[tier];
                              const cfg = tripForm.age_pricing?.[tier] || {};
                              const sellP = parseFloat(cfg.sell) || 0;
                              const costP = parseFloat(cfg.cost) || 0;
                              const count = parseInt(tripForm.age_tier_counts?.[tier] || 0, 10);
                              const subtotalSell = count * sellP;
                              return (
                                <div
                                  key={tier}
                                  className={`border-2 rounded-md p-2 text-xs ${count > 0 ? 'border-emerald-300 bg-emerald-50/70' : 'border-gray-200 bg-white'}`}
                                  data-testid={`tier-card-${tier}`}
                                >
                                  <div className="font-bold text-gray-800 mb-0.5">{meta.label}</div>
                                  <div className="text-[10px] text-gray-500 mb-1.5">
                                    السعر: {sellP.toLocaleString()} دج
                                    {tripForm.service_origin === 'supplier' && (
                                      <span className="text-red-600 mr-1">· تكلفة: {costP.toLocaleString()}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 justify-center">
                                    <button
                                      type="button"
                                      onClick={() => bumpTierCount(tier, -1)}
                                      disabled={count === 0}
                                      className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 font-bold text-gray-700"
                                      data-testid={`tier-minus-${tier}`}
                                    >−</button>
                                    <Input
                                      type="number"
                                      min={0}
                                      value={count}
                                      onChange={(e) => {
                                        const target = Math.max(0, parseInt(e.target.value || 0, 10) || 0);
                                        bumpTierCount(tier, target - count);
                                      }}
                                      className="h-7 text-center text-sm font-bold w-16"
                                      data-testid={`tier-count-${tier}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => bumpTierCount(tier, +1)}
                                      className="w-7 h-7 rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold"
                                      data-testid={`tier-plus-${tier}`}
                                    >+</button>
                                  </div>
                                  {count > 0 && (
                                    <div className="text-[11px] text-emerald-700 font-bold text-center mt-1.5">
                                      المجموع: {subtotalSell.toLocaleString()} دج
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-gray-500 italic mt-2">
                            💡 بيانات كل عميل موجودة في ملف الزبون — هنا فقط تحدّد كم شخص في كل شريحة.
                            المورد يحاسَب على مجموع تكلفة الشرائح تلقائياً.
                          </p>
                        </div>

                        {/* Live totals */}
                        {totalAgeBasedPax() > 0 && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold" data-testid="age-pricing-totals">
                            <span className="text-emerald-800">
                              💰 إجمالي البيع: <span className="font-mono" data-testid="age-total-sell">{calculateFinalAmount().toLocaleString()} دج</span>
                            </span>
                            {tripForm.service_origin === 'supplier' && (
                              <>
                                <span className="text-red-700">
                                  🧾 إجمالي تكلفة المورد: <span className="font-mono" data-testid="age-total-cost">{calculateAgeBasedSupplierCost().toLocaleString()} دج</span>
                                </span>
                                <span className="text-brand-700">
                                  📈 الربح: <span className="font-mono">{(calculateFinalAmount() - calculateAgeBasedSupplierCost()).toLocaleString()} دج</span>
                                </span>
                              </>
                            )}
                            <span className="text-gray-500">| عدد المسافرين: {totalAgeBasedPax()}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 🆕 Supplier Information — unified with the suppliers catalog.
                    Hidden for subagent users (always internal Sanhaja service). */}
                {!user?.is_subagent && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-800 mb-3">📦 مصدر الخدمة</h4>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-3" data-testid="trip-origin-block">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setTripForm({ ...tripForm, service_origin: 'internal', supplier_id: '', supplier_cost: '', supplier_name: 'صنهاجة (داخلي)' })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${tripForm.service_origin === 'internal' ? 'bg-green-100 border-green-400 text-green-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                        data-testid="trip-origin-internal-btn"
                      >🏢 خدمة صنهاجة</button>
                      <button
                        type="button"
                        onClick={() => setTripForm({ ...tripForm, service_origin: 'supplier', supplier_name: '' })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${tripForm.service_origin === 'supplier' ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                        data-testid="trip-origin-supplier-btn"
                      >📦 خدمة مورد</button>
                    </div>

                    {tripForm.service_origin === 'supplier' && (
                      <div className="space-y-2 pt-1">
                        <div>
                          <Label className="text-xs">المورد *</Label>
                          <select
                            value={tripForm.supplier_id}
                            onChange={e => {
                              const sid = e.target.value;
                              const s = suppliers.find(x => x.id === sid);
                              setTripForm({
                                ...tripForm,
                                supplier_id: sid,
                                // Keep legacy supplier_name in sync (used by backend filter + legacy reports)
                                supplier_name: s?.name || '',
                                supplier_contact: s ? `${s.phone || ''} ${s.email || ''}`.trim() : tripForm.supplier_contact,
                              });
                            }}
                            className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                            data-testid="trip-supplier-select"
                          >
                            <option value="">اختر مورداً...</option>
                            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          {suppliers.length === 0 && (
                            <p className="text-xs text-orange-600 mt-1">⚠️ لا يوجد موردون. أضف مورداً أولاً من "إدارة الموردين".</p>
                          )}
                        </div>
                        {/* 🆕 Transparency: supplier cost is visible to
                            every employee so the team can audit each other.
                            In age-based mode the total supplier cost is
                            derived from the per-tier cost × head-count grid
                            (see calculateAgeBasedSupplierCost). We hide the
                            manual input to avoid the historic bug where the
                            base service price (computed_price) was mixed
                            with the tier-based totals. */}
                        {tripForm.pricing_mode === 'age_based' ? (
                          <div className="bg-white/70 border border-yellow-300 rounded-md p-2 text-xs space-y-1" data-testid="age-based-supplier-cost-info">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700 font-semibold">🧾 إجمالي تكلفة المورد (محسوب تلقائياً)</span>
                              <span className="font-mono font-bold text-red-700">
                                {calculateAgeBasedSupplierCost().toLocaleString()} دج
                              </span>
                            </div>
                            {calculateFinalAmount() > 0 && (
                              <div className="flex items-center justify-between border-t border-yellow-200 pt-1">
                                <span className="text-gray-700 font-semibold">💰 الربح المتوقع</span>
                                <span className={`font-mono font-bold ${(calculateFinalAmount() - calculateAgeBasedSupplierCost()) >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="age-based-profit">
                                  {(calculateFinalAmount() - calculateAgeBasedSupplierCost()).toLocaleString()} دج
                                </span>
                              </div>
                            )}
                            <p className="text-[10px] text-gray-500 italic">
                              💡 التكلفة محسوبة من جدول الأسعار حسب الفئة العمرية × عدد الأشخاص لكل شريحة.
                            </p>
                          </div>
                        ) : (
                          <div>
                            <Label className="text-xs">تكلفة المورد (دج) *</Label>
                            <Input
                              type="number"
                              value={tripForm.supplier_cost}
                              onChange={e => setTripForm({ ...tripForm, supplier_cost: e.target.value })}
                              placeholder="ما تدفعه الوكالة للمورد"
                              data-testid="trip-supplier-cost"
                            />
                            {tripForm.supplier_cost && tripForm.computed_price > 0 && (
                              <p className="text-xs mt-1 text-green-700">
                                💰 الربح: {(parseFloat(tripForm.computed_price || 0) - parseFloat(tripForm.supplier_cost || 0)).toLocaleString()} دج
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {tripForm.service_origin === 'internal' && (
                      <p className="text-xs text-gray-600">الرحلة من خدمات صنهاجة الداخلية — لن تسجل أي مستحقات لمورد.</p>
                    )}
                  </div>

                  {/* Legacy fallback: when origin === supplier and the supplier is picked,
                       we keep the legacy supplier_name field hidden (auto-synced).
                       For 'internal', we still need a non-empty supplier_name because the
                       backend currently requires it. */}
                  {tripForm.service_origin === 'internal' && (
                    <div className="mt-2 hidden">
                      <Input value={tripForm.supplier_name || 'صنهاجة (داخلي)'} readOnly />
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Column 2: Customer Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-green-800 border-b pb-1">👤 معلومات العميل</h3>
                
                <div>
                  <Label className="text-sm font-medium">اختيار العميل *</Label>
                  <SearchableSelect
                    value={tripForm.client_id || 'new_client'}
                    onChange={(v) => handleClientSelectionChange(v || 'new_client')}
                    options={[
                      { value: 'new_client', label: '➕ عميل جديد', hint: 'إدخال بيانات يدوية', search: 'new عميل جديد جديده add' },
                      ...clients.map(client => ({
                        value: client.id,
                        label: client.name,
                        hint: `${client.phone || ''}${client.cin_passport ? ` • ${client.cin_passport}` : ''}`,
                        search: `${client.name} ${client.phone || ''} ${client.cin_passport || ''}`,
                      })),
                    ]}
                    placeholder="اختر عميلاً أو ابحث بالاسم/الهاتف..."
                    searchPlaceholder="🔎 ابحث بالاسم، الهاتف أو الهوية..."
                    emptyText="لا يوجد عميل مطابق"
                    allowClear={false}
                    testId="ot-client-select"
                  />
                  {clientsLoading && (
                    <p className="text-xs text-green-600 mt-1">جاري تحميل العملاء...</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm">اسم العميل *</Label>
                  <Input
                    value={tripForm.client_name}
                    onChange={(e) => setTripForm({...tripForm, client_name: e.target.value})}
                    placeholder="الاسم الكامل للعميل"
                    disabled={!showNewClientFields}
                    className={showNewClientFields ? "" : "bg-gray-100"}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">رقم الهاتف</Label>
                  <Input
                    value={tripForm.client_phone}
                    onChange={(e) => setTripForm({...tripForm, client_phone: e.target.value})}
                    placeholder="رقم الهاتف"
                    disabled={!showNewClientFields}
                    className={showNewClientFields ? "" : "bg-gray-100"}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">البريد الإلكتروني</Label>
                  <Input
                    type="email"
                    value={tripForm.client_email}
                    onChange={(e) => setTripForm({...tripForm, client_email: e.target.value})}
                    placeholder="البريد الإلكتروني (اختياري)"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">رقم الهوية/جواز السفر</Label>
                  <Input
                    value={tripForm.client_cin_passport}
                    onChange={(e) => setTripForm({...tripForm, client_cin_passport: e.target.value})}
                    placeholder="رقم الهوية أو جواز السفر"
                    disabled={!showNewClientFields}
                    className={showNewClientFields ? "" : "bg-gray-100"}
                  />
                </div>

                <div className="bg-green-100 p-2 rounded-md">
                  <p className="text-xs text-green-700">
                    💡 يمكنك اختيار عميل موجود من القائمة أو إدخال معلومات عميل جديد
                  </p>
                </div>
              </div>

              {/* Column 3: Financial Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-orange-800 border-b pb-1">💰 المعلومات المالية</h3>
                
                {/* Base Price Display */}
                {tripForm.service_id && (
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <Label className="text-sm text-brand-800">
                      {tripForm.pricing_mode === 'age_based'
                        ? 'إجمالي البيع (حسب الفئات العمرية):'
                        : 'المبلغ الأساسي من الخدمة:'}
                    </Label>
                    <div className="text-xl font-bold text-brand-600" data-testid="trip-base-price-display">
                      {(tripForm.pricing_mode === 'age_based'
                        ? calculateFinalAmount()
                        : (tripForm.computed_price > 0
                            ? tripForm.computed_price
                            : (services.find(s => s.id === tripForm.service_id)?.base_price || 0))
                      ).toLocaleString()} دج
                    </div>
                    {tripForm.service_label && (
                      <p className="text-xs text-gray-600 mt-1">{tripForm.service_label}</p>
                    )}
                  </div>
                )}

                {/* Discount Section */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <Label className="font-medium text-red-800 mb-2 block">🔽 التخفيض</Label>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      value={tripForm.discount_amount}
                      onChange={(e) => setTripForm({...tripForm, discount_amount: e.target.value})}
                      placeholder="مبلغ التخفيض (اختياري)"
                    />
                    {parseFloat(tripForm.discount_amount) > 0 && (
                      <Textarea
                        value={tripForm.discount_reason}
                        onChange={(e) => setTripForm({...tripForm, discount_reason: e.target.value})}
                        placeholder="سبب التخفيض (إجباري عند وجود تخفيض)"
                        rows={2}
                        className="border-red-300"
                      />
                    )}
                  </div>
                </div>

                {/* Commission Section */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <Label className="font-medium text-green-800 mb-2 block">🔼 العمولة</Label>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      value={tripForm.commission_amount}
                      onChange={(e) => setTripForm({...tripForm, commission_amount: e.target.value})}
                      placeholder="مبلغ العمولة (اختياري)"
                    />
                    {parseFloat(tripForm.commission_amount) > 0 && (
                      <Textarea
                        value={tripForm.commission_reason}
                        onChange={(e) => setTripForm({...tripForm, commission_reason: e.target.value})}
                        placeholder="سبب العمولة (إجباري عند وجود عمولة)"
                        rows={2}
                        className="border-green-300"
                      />
                    )}
                  </div>
                </div>

                {/* 🆕 Airline-discount eligibility — مجاهد / ابن شهيد / أخرى */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="trip-airline-discount-block">
                  <Label className="font-medium text-emerald-900 mb-2 block">
                    🎖️ تخفيض الطيران (مستفيد من تخفيض شركة الطيران)
                  </Label>
                  <p className="text-xs text-emerald-700 mb-2">
                    إذا كان العميل / المعتمر مستفيداً من تخفيض شركة الطيران (مجاهد، ابن شهيد، أخرى)،
                    حدّد فئته وأرفق سند الجهة المختصة. سيتم تطبيق السعر المخفّض تلقائياً عند تكلفة التذكرة في الرحلة الجوية المرتبطة.
                  </p>
                  <div className="space-y-2">
                    <select
                      value={tripForm.discount_category}
                      onChange={(e) => setTripForm({...tripForm, discount_category: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md bg-white"
                      data-testid="trip-discount-category-select"
                    >
                      <option value="">— بدون تخفيض طيران —</option>
                      <option value="مجاهد">مجاهد</option>
                      <option value="ابن شهيد">ابن شهيد</option>
                      <option value="أخرى">أخرى</option>
                    </select>
                    {tripForm.discount_category && (
                      <div className="bg-white border border-emerald-300 rounded p-2">
                        <Label className="text-xs font-bold text-emerald-900 mb-1 block">
                          📎 صورة سند التخفيض (JPG / PNG / PDF — أقل من 4MB)
                        </Label>
                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg,application/pdf"
                          onChange={(e) => setTripForm({...tripForm, discount_document_file: e.target.files?.[0] || null})}
                          data-testid="trip-discount-document-input"
                          className="text-xs"
                        />
                        {tripForm.discount_document_file && (
                          <div className="text-xs text-emerald-700 mt-1">
                            ✅ مُحدّد: {tripForm.discount_document_file.name}
                          </div>
                        )}
                      </div>
                    )}
                    {tripForm.discount_category && linkedFlightDiscount?.linked && linkedFlightDiscount.discount_amount > 0 && (
                      <div className="bg-blue-50 border border-blue-300 rounded p-2 text-xs" data-testid="trip-flight-discount-hint">
                        💡 الرحلة الجوية <strong>{linkedFlightDiscount.flight_no}</strong> ({linkedFlightDiscount.airline_name})
                        تطبّق تخفيضاً قدره <strong>{Number(linkedFlightDiscount.discount_amount).toLocaleString()} دج</strong> للراكب الواحد
                        (السعر الأصلي {Number(linkedFlightDiscount.price_adult_with_seat).toLocaleString()} → بعد التخفيض {Number(linkedFlightDiscount.price_discounted).toLocaleString()}).
                        تم تطبيق نفس المبلغ تلقائياً في خانة «التخفيض» أعلاه — يمكنك تعديله.
                      </div>
                    )}
                    {tripForm.discount_category && (!linkedFlightDiscount || !linkedFlightDiscount.linked || linkedFlightDiscount.discount_amount === 0) && (
                      <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs">
                        ⚠️ لا توجد رحلة جوية مرتبطة بهذا الفوج (أو لم يُسجَّل سعر تخفيض على الرحلة الجوية).
                        أدخل قيمة التخفيض يدوياً في الخانة المخصصة لها أعلاه.
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Amount Calculation */}
                {tripForm.service_id && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-bold text-yellow-800 mb-3">💵 حساب المبلغ النهائي:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>المبلغ الأساسي:</span>
                        <span className="font-bold text-brand-600">
                          {services.find(s => s.id === tripForm.service_id)?.base_price?.toLocaleString() || 0} دج
                        </span>
                      </div>
                      {parseFloat(tripForm.discount_amount) > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>التخفيض:</span>
                          <span className="font-bold">- {parseFloat(tripForm.discount_amount).toLocaleString()} دج</span>
                        </div>
                      )}
                      {parseFloat(tripForm.commission_amount) > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>العمولة:</span>
                          <span className="font-bold">+ {parseFloat(tripForm.commission_amount).toLocaleString()} دج</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2 text-lg">
                        <span className="font-bold">المبلغ النهائي:</span>
                        <span className="font-bold text-orange-600">
                          {calculateFinalAmount().toLocaleString()} دج
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">📋 ملاحظات مهمة:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• بعد إنشاء الرحلة ستحتاج لإضافة الدفعات</li>
                    <li>• كل دفعة تحتاج وثيقة (صورة أو PDF)</li>
                    <li>• سيتم إنشاء رقم وصل رئيسي للرحلة</li>
                    <li>• كل دفعة ستأخذ رقم فرعي مثل A-2025-001/1</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 border-t pt-4">
              <Button variant="outline" onClick={() => {
                setShowAddTripDialog(false);
                resetTripForm();
              }}>
                إلغاء
              </Button>
              <Button onClick={createTrip} className="bg-green-600 hover:bg-green-700">
                🗺️ إنشاء الرحلة المنظمة
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog - Add Payment to Trip */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>💰 إضافة دفعة للرحلة</DialogTitle>
            <DialogDescription>
              إضافة دفعة جديدة مع وثيقة الوصل الإجبارية
            </DialogDescription>
          </DialogHeader>
          
          {selectedTripForPayment && (
            <div className="space-y-4">
              {/* Trip Information */}
              <div className="bg-brand-50 p-3 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-2">معلومات الرحلة:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>رقم الرحلة: <strong>{selectedTripForPayment.trip_no}</strong></div>
                  <div>العميل: <strong>{selectedTripForPayment.client_name}</strong></div>
                  <div>الوجهة: <strong>{selectedTripForPayment.destination}</strong></div>
                  <div>المبلغ المتبقي: <strong className="text-orange-600">{selectedTripForPayment.remaining_amount?.toLocaleString()} دج</strong></div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-3">
                <div>
                  <Label>مبلغ الدفعة (دج) *</Label>
                  <Input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                    placeholder="مبلغ الدفعة"
                    max={selectedTripForPayment.remaining_amount}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    الحد الأقصى: {selectedTripForPayment.remaining_amount?.toLocaleString()} دج
                  </p>
                </div>

                <div>
                  <Label>تاريخ الدفع *</Label>
                  <Input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>طريقة الدفع</Label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="cash">💵 نقدي</option>
                    <option value="bank">🏦 بنكي</option>
                    <option value="card">💳 بطاقة</option>
                  </select>
                </div>

                <div>
                  <Label>ملاحظات الدفعة</Label>
                  <Textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                    placeholder="ملاحظات إضافية (اختياري)"
                    rows={2}
                  />
                </div>

                {/* Document Upload (Required) */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <Label className="font-medium text-orange-800 mb-2 block">📄 وثيقة وصل الدفع (إجباري) *</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validate file type
                        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
                        if (!allowedTypes.includes(file.type)) {
                          alert('نوع الملف غير مدعوم. يجب أن يكون صورة أو PDF');
                          return;
                        }
                        
                        // Validate file size (5MB max)
                        if (file.size > 5 * 1024 * 1024) {
                          alert('حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت');
                          return;
                        }
                        
                        setPaymentForm({...paymentForm, document_file: file});
                        setDocumentType(file.type === 'application/pdf' ? 'pdf' : 'image');
                        
                        // Create preview for images
                        if (file.type.startsWith('image/')) {
                          const reader = new FileReader();
                          reader.onload = (e) => setDocumentPreview(e.target.result);
                          reader.readAsDataURL(file);
                        } else {
                          setDocumentPreview(null);
                        }
                      } else {
                        setPaymentForm({...paymentForm, document_file: null});
                        setDocumentPreview(null);
                        setDocumentType('');
                      }
                    }}
                    className="cursor-pointer"
                    required
                  />
                  
                  <div className="text-xs text-orange-700 mt-2">
                    <p>📷 الملفات المدعومة: صور (JPG, PNG, GIF, WebP) أو ملف PDF</p>
                    <p>📐 الحد الأقصى: 5 ميجابايت</p>
                    <p className="font-semibold text-red-600">⚠️ رفع وثيقة الوصل إجباري</p>
                  </div>
                  
                  {/* Document Preview */}
                  {documentPreview && documentType === 'image' && (
                    <div className="mt-3">
                      <Label className="text-sm text-gray-700 mb-2 block">معاينة الوثيقة:</Label>
                      <img 
                        src={documentPreview} 
                        alt="معاينة وصل الدفع" 
                        className="w-full max-h-32 object-contain rounded border"
                      />
                    </div>
                  )}
                  
                  {paymentForm.document_file && documentType === 'pdf' && (
                    <div className="mt-3 p-2 bg-red-50 rounded border border-red-200">
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600">📄</span>
                        <span className="text-sm text-red-800">
                          PDF: {paymentForm.document_file.name}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => {
                  setShowPaymentDialog(false);
                  setPaymentForm({
                    amount: '',
                    payment_date: new Date().toISOString().split('T')[0],
                    payment_method: 'cash',
                    notes: '',
                    document_file: null
                  });
                  setDocumentPreview(null);
                  setDocumentType('');
                }}>
                  إلغاء
                </Button>
                
                {/* Enhanced Payment Button with Status Indicator */}
                <div className="relative">
                  <Button 
                    onClick={addPayment}
                    className={`${
                      (!paymentForm.amount || !paymentForm.document_file) 
                        ? "bg-gray-400 cursor-not-allowed" 
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                    disabled={!paymentForm.amount || !paymentForm.document_file}
                    title={
                      !paymentForm.amount && !paymentForm.document_file 
                        ? "يرجى إدخال مبلغ الدفعة ورفع وثيقة الوصل"
                        : !paymentForm.amount 
                        ? "يرجى إدخال مبلغ الدفعة" 
                        : !paymentForm.document_file 
                        ? "يرجى رفع وثيقة الوصل"
                        : "جاهز لإضافة الدفعة"
                    }
                  >
                    💰 إضافة الدفعة
                  </Button>
                  
                  {/* Status indicator below button */}
                  {(!paymentForm.amount || !paymentForm.document_file) && (
                    <div className="absolute -bottom-8 left-0 right-0">
                      <p className="text-xs text-red-600 text-center">
                        {!paymentForm.amount && !paymentForm.document_file 
                          ? "⚠️ مطلوب: مبلغ + وثيقة"
                          : !paymentForm.amount 
                          ? "⚠️ مطلوب: مبلغ الدفعة" 
                          : "⚠️ مطلوب: وثيقة الوصل"
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 2026-07-22: Convert Remaining Amount → Installments Dialog
          (mirrors the Daily Operations flow so accountants have the
          same UX regardless of where the sale originated). */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md" data-testid="trip-convert-installments-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">💳 تحويل المبلغ المتبقي إلى الأقساط</DialogTitle>
            <DialogDescription className="text-right text-xs text-gray-600">
              أنشئ وصل أقساط جديد للمبلغ المتبقي على هذه الرحلة. ستُعلَّم الرحلة بإشارة «تحت الأقساط».
            </DialogDescription>
          </DialogHeader>
          {convertTrip && (
            <div className="space-y-4 py-2">
              <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
                <div><strong>الرحلة:</strong> {convertTrip.trip_no}</div>
                <div><strong>العميل:</strong> {convertTrip.client_name || '-'}</div>
                <div><strong>الوجهة:</strong> {convertTrip.destination || '-'}</div>
                <div className="mt-1 text-purple-800">
                  <strong>المبلغ المراد تحويله:</strong>{' '}
                  <span data-testid="trip-convert-remaining-display">
                    {convertRemaining.toLocaleString()} دج
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-right block mb-1">عدد الأقساط</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={convertForm.number_of_payments}
                  onChange={e => setConvertForm({ ...convertForm, number_of_payments: e.target.value, custom_dates: [] })}
                  className="text-right"
                  data-testid="trip-convert-payments-count"
                />
              </div>

              <div>
                <Label className="text-right block mb-1">تكرار الدفع</Label>
                <select
                  value={convertForm.payment_frequency}
                  onChange={e => setConvertForm({ ...convertForm, payment_frequency: e.target.value, custom_dates: [] })}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="trip-convert-frequency"
                >
                  <option value="weekly">أسبوعياً</option>
                  <option value="monthly">شهرياً</option>
                  <option value="quarterly">كل 3 أشهر</option>
                  <option value="custom">تواريخ مخصصة</option>
                </select>
              </div>

              {convertForm.payment_frequency !== 'custom' && (
                <div>
                  <Label className="text-right block mb-1">تاريخ بداية أول قسط</Label>
                  <Input
                    type="date"
                    value={convertForm.start_date}
                    onChange={e => setConvertForm({ ...convertForm, start_date: e.target.value })}
                    data-testid="trip-convert-start-date"
                  />
                </div>
              )}

              {convertForm.payment_frequency === 'custom' && (
                <div className="space-y-2">
                  <Label className="text-right block">تواريخ الأقساط (مخصصة):</Label>
                  {Array.from({ length: Number(convertForm.number_of_payments) || 0 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 w-16">قسط {i + 1}:</span>
                      <Input
                        type="date"
                        value={convertForm.custom_dates[i] || ''}
                        onChange={e => {
                          const arr = [...(convertForm.custom_dates || [])];
                          arr[i] = e.target.value;
                          setConvertForm({ ...convertForm, custom_dates: arr });
                        }}
                        data-testid={`trip-convert-custom-date-${i}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-gray-50 rounded p-2 text-xs text-gray-600">
                💡 سيُنشأ وصل أقساط جديد بالمبلغ المتبقي، وستظل الرحلة ظاهرة مع علامة «تحت الأقساط».
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowConvertDialog(false)}>إلغاء</Button>
                <Button
                  onClick={handleConvertSubmit}
                  disabled={converting}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="trip-convert-submit-btn"
                >
                  {converting ? 'جاري التحويل...' : '💳 تحويل إلى الأقساط'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Change Service Dialog - Compact Size */}
      <Dialog open={showChangeServiceDialog} onOpenChange={setShowChangeServiceDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔄 تغيير خدمة الرحلة</DialogTitle>
            <DialogDescription>
              تغيير الخدمة مع حساب الفروقات
            </DialogDescription>
          </DialogHeader>
          
          {selectedTripForServiceChange && (
            <div className="space-y-4">
              {/* Compact Trip Information */}
              <div className="bg-brand-50 p-3 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-2 text-sm">🗺️ معلومات الرحلة:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>الرحلة: <strong>{selectedTripForServiceChange.trip_no}</strong></div>
                  <div>العميل: <strong>{selectedTripForServiceChange.client_name}</strong></div>
                </div>
                <div className="mt-2 text-xs">
                  <div>الخدمة الحالية: <strong>{selectedTripForServiceChange.service_name}</strong></div>
                  <div className="flex justify-between mt-1">
                    <span>السعر: <strong className="text-brand-600">{selectedTripForServiceChange.base_price?.toLocaleString()} دج</strong></span>
                    <span>المدفوع: <strong className="text-green-600">{selectedTripForServiceChange.total_paid?.toLocaleString()} دج</strong></span>
                  </div>
                </div>
              </div>

              {/* Service Selection */}
              <div className="space-y-3">
                {/* 🆕 Service kind switcher */}
                <div className="grid grid-cols-2 gap-2" data-testid="service-kind-switcher">
                  <button
                    type="button"
                    onClick={() => setServiceChangeForm({
                      ...serviceChangeForm,
                      service_kind: 'simple',
                      new_service_id: '',
                      computed_price: 0,
                      new_service_label: '',
                      hotel_id: null, room_id: null, has_meals: false,
                    })}
                    className={`p-2 rounded-md text-sm border transition ${
                      serviceChangeForm.service_kind === 'simple'
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'
                    }`}
                    data-testid="service-kind-simple"
                  >
                    🧾 خدمة بسيطة
                  </button>
                  <button
                    type="button"
                    onClick={() => setServiceChangeForm({
                      ...serviceChangeForm,
                      service_kind: 'hierarchical',
                      new_service_id: '',
                      computed_price: 0,
                      new_service_label: '',
                      hotel_id: null, room_id: null, has_meals: false,
                    })}
                    className={`p-2 rounded-md text-sm border transition ${
                      serviceChangeForm.service_kind === 'hierarchical'
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'
                    }`}
                    data-testid="service-kind-hierarchical"
                  >
                    🏨 خدمة هرمية (فندق + غرفة)
                  </button>
                </div>

                {serviceChangeForm.service_kind === 'simple' ? (
                  <div>
                    <Label className="text-sm font-medium text-orange-800">الخدمة الجديدة *</Label>
                    <SearchableSelect
                      value={serviceChangeForm.new_service_id}
                      onChange={(v) => setServiceChangeForm({...serviceChangeForm, new_service_id: v})}
                      options={services
                        .filter(service => service.id !== selectedTripForServiceChange.service_id)
                        .map(service => ({
                          value: service.id,
                          label: service.name,
                          hint: `${service.base_price?.toLocaleString() || 0} دج`,
                          search: `${service.name} ${service.description || ''}`,
                        }))
                      }
                      placeholder="اختر الخدمة الجديدة أو ابحث..."
                      searchPlaceholder="🔎 ابحث باسم الخدمة..."
                      emptyText="لا توجد خدمة مطابقة"
                      testId="change-service-simple-select"
                    />
                  </div>
                ) : (
                  <div data-testid="change-service-hierarchical-picker">
                    <Label className="text-sm font-medium text-orange-800 mb-1 block">
                      اختر الخدمة الهرمية (الخدمة → الفندق → الغرفة → إعاشة) *
                    </Label>
                    <CascadingServiceSelector
                      compact
                      required
                      value={{
                        service_id: serviceChangeForm.new_service_id,
                        hotel_id: serviceChangeForm.hotel_id,
                        room_id: serviceChangeForm.room_id,
                        has_meals: serviceChangeForm.has_meals,
                      }}
                      onChange={(v) => setServiceChangeForm({
                        ...serviceChangeForm,
                        new_service_id: v.service_id || '',
                        hotel_id: v.hotel_id || null,
                        room_id: v.room_id || null,
                        has_meals: !!v.has_meals,
                        computed_price: v.total_price || 0,
                        new_service_label: v.service_label || '',
                      })}
                    />
                    {serviceChangeForm.computed_price > 0 && (
                      <div className="mt-2 p-2 bg-orange-50 rounded text-xs text-orange-900">
                        💰 السعر النهائي للخدمة الهرمية:{' '}
                        <strong>{serviceChangeForm.computed_price.toLocaleString()} دج</strong>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium text-orange-800">سبب التغيير *</Label>
                  <Textarea
                    value={serviceChangeForm.reason}
                    onChange={(e) => setServiceChangeForm({...serviceChangeForm, reason: e.target.value})}
                    placeholder="سبب تغيير الخدمة"
                    rows={2}
                    className="border-orange-300 text-sm"
                  />
                </div>

                {/* 🆕 Deduction source — only meaningful when a refund or extra payment is involved */}
                <div className="p-3 border border-orange-200 bg-orange-50/30 rounded-md">
                  <Label className="text-sm font-medium text-orange-900">
                    💰 مصدر الفرق المالي *
                  </Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <label className={`cursor-pointer rounded-md border-2 p-2 text-sm flex items-start gap-2 ${serviceChangeForm.deduction_source === 'cashbox' ? 'border-orange-500 bg-orange-100/70' : 'border-gray-300 bg-white'}`}>
                      <input
                        type="radio"
                        name="cs_deduction"
                        checked={serviceChangeForm.deduction_source === 'cashbox'}
                        onChange={() => setServiceChangeForm({...serviceChangeForm, deduction_source: 'cashbox'})}
                        data-testid="cs-src-cashbox"
                        className="mt-1"
                      />
                      <div>
                        <div className="font-semibold">🏦 من الخزينة المركزية</div>
                        <div className="text-[11px] text-gray-600">المبلغ كان قد حُوِّل من الوكالة إلى الخزينة العامة</div>
                      </div>
                    </label>
                    <label className={`cursor-pointer rounded-md border-2 p-2 text-sm flex items-start gap-2 ${serviceChangeForm.deduction_source === 'agency_balance' ? 'border-orange-500 bg-orange-100/70' : 'border-gray-300 bg-white'}`}>
                      <input
                        type="radio"
                        name="cs_deduction"
                        checked={serviceChangeForm.deduction_source === 'agency_balance'}
                        onChange={() => setServiceChangeForm({...serviceChangeForm, deduction_source: 'agency_balance'})}
                        data-testid="cs-src-agency"
                        className="mt-1"
                      />
                      <div>
                        <div className="font-semibold">🏢 من رصيد الوكالة</div>
                        <div className="text-[11px] text-gray-600">المبلغ لا يزال لدى الوكالة (لم يُحوَّل بعد)</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* 🆕 Mandatory proof document */}
                <div className="p-3 border-2 border-dashed border-orange-300 bg-orange-50/50 rounded-md">
                  <Label className="text-sm font-medium text-orange-900">
                    📎 وثيقة الإثبات * <span className="text-xs text-gray-600">(صورة JPG/PNG/WebP أو PDF — حد أقصى 5MB)</span>
                  </Label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setServiceChangeForm({...serviceChangeForm, document: f});
                    }}
                    className="mt-2 block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-orange-600 file:text-white hover:file:bg-orange-700"
                    data-testid="change-service-document-input"
                  />
                  {serviceChangeForm.document && (
                    <div className="mt-2 text-xs text-green-700 flex items-center gap-2">
                      ✅ <span>{serviceChangeForm.document.name}</span>
                      <span className="text-gray-500">({(serviceChangeForm.document.size / 1024).toFixed(0)} KB)</span>
                    </div>
                  )}
                  {!serviceChangeForm.document && (
                    <p className="mt-2 text-xs text-red-600">⚠️ لا يمكن تطبيق التغيير بدون وثيقة الإثبات</p>
                  )}
                </div>
              </div>

              {/* Compact Financial Prediction */}
              {serviceChangePrediction && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <h4 className="font-bold text-yellow-800 mb-2 text-sm">💰 النتيجة المالية:</h4>
                  
                  <div className="space-y-2">
                    {/* Price Change Summary */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>السعر السابق: <strong>{serviceChangePrediction.oldBasePrice?.toLocaleString()} دج</strong></div>
                      <div>السعر الجديد: <strong>{serviceChangePrediction.newBasePrice?.toLocaleString()} دج</strong></div>
                    </div>

                    {/* Price Difference Highlight */}
                    <div className={`p-2 rounded text-center ${
                      serviceChangePrediction.priceDifference > 0 
                        ? 'bg-orange-100 text-orange-800' 
                        : serviceChangePrediction.priceDifference < 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <div className="text-sm font-bold">
                        فرق السعر: {serviceChangePrediction.priceDifference > 0 ? '+' : ''}
                        {serviceChangePrediction.priceDifference?.toLocaleString()} دج
                      </div>
                    </div>

                    {/* Action Required */}
                    {serviceChangePrediction.overpaymentAmount > 0 && (
                      <div className="bg-red-100 text-red-800 p-2 rounded text-center text-sm">
                        <div className="font-bold">💵 تعويض للعميل: {serviceChangePrediction.overpaymentAmount?.toLocaleString()} دج</div>
                        <div className="text-xs">سيتم إنشاء عملية تعويض</div>
                      </div>
                    )}

                    {serviceChangePrediction.needsAdditionalPayment && (
                      <div className="bg-orange-100 text-orange-800 p-2 rounded text-center text-sm">
                        <div className="font-bold">💳 دفعة إضافية: {serviceChangePrediction.newRemainingAmount?.toLocaleString()} دج</div>
                        <div className="text-xs">يمكن إضافتها بعد التغيير</div>
                      </div>
                    )}

                    {serviceChangePrediction.newRemainingAmount === 0 && (
                      <div className="bg-green-100 text-green-800 p-2 rounded text-center text-sm">
                        <div className="font-bold">✅ مدفوع كاملاً</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Compact Action Buttons */}
              <div className="flex justify-end space-x-2 pt-3 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowChangeServiceDialog(false);
                    setServiceChangeForm({
                      new_service_id: '',
                      reason: '',
                      service_kind: 'simple',
                      computed_price: 0,
                      new_service_label: '',
                      hotel_id: null, room_id: null, has_meals: false,
                      document: null,
                      deduction_source: 'cashbox',
                    });
                    setServiceChangePrediction(null);
                  }}
                >
                  إلغاء
                </Button>
                
                <Button 
                  onClick={changeService}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700"
                  disabled={
                    !serviceChangeForm.new_service_id
                    || !serviceChangeForm.reason.trim()
                    || !serviceChangeForm.document
                    || (serviceChangeForm.service_kind === 'hierarchical'
                        && !(serviceChangeForm.computed_price > 0))
                  }
                >
                  {(user?.role === 'super_admin' || user?.role === 'general_accountant')
                    ? '🔄 تطبيق التغيير'
                    : '📤 إرسال طلب التغيير للموافقة'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Viewing Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>📄 عرض الوثيقة</DialogTitle>
            <DialogDescription>{modalImageTitle}</DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center">
            <img 
              src={modalImageUrl}
              alt={modalImageTitle}
              className="max-w-full max-h-[70vh] object-contain rounded-lg border"
            />
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setShowDocumentModal(false)}>
              إغلاق
            </Button>
            <Button 
              onClick={() => window.open(modalImageUrl, '_blank')}
              className="bg-brand-600 hover:bg-brand-700"
            >
              🔍 فتح في نافذة جديدة
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trip Payments Details Modal */}
      <Dialog open={showPaymentDetailsDialog} onOpenChange={setShowPaymentDetailsDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📋 تفاصيل دفعات الرحلة</DialogTitle>
            <DialogDescription>
              جميع الدفعات مع أرقام الوصولات والوثائق المرفقة
            </DialogDescription>
          </DialogHeader>
          
          {selectedTripPayments && (
            <div className="space-y-6">
              {/* Trip Summary */}
              <div className="bg-brand-50 p-4 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-3">معلومات الرحلة:</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">رقم الرحلة:</span>
                    <strong className="ml-2 text-brand-600">{selectedTripPayments.trip_no}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">العميل:</span>
                    <strong className="ml-2">{selectedTripPayments.client_name}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">الوجهة:</span>
                    <strong className="ml-2">{selectedTripPayments.destination}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">المبلغ النهائي:</span>
                    <strong className="ml-2 text-brand-600">{selectedTripPayments.final_amount?.toLocaleString()} دج</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">إجمالي المدفوع:</span>
                    <strong className="ml-2 text-green-600">{selectedTripPayments.total_paid?.toLocaleString()} دج</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">المبلغ المتبقي:</span>
                    <strong className="ml-2 text-orange-600">{selectedTripPayments.remaining_amount?.toLocaleString()} دج</strong>
                  </div>
                </div>
              </div>

              {/* Payments List */}
              {paymentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">جاري تحميل الدفعات...</p>
                </div>
              ) : tripPayments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>لا توجد دفعات مسجلة لهذه الرحلة</p>
                  <p className="text-sm">اضغط "💰 دفعة" لإضافة دفعة جديدة</p>
                </div>
              ) : (
                <div>
                  <h4 className="font-bold text-gray-800 mb-4">📋 قائمة الدفعات ({tripPayments.length}):</h4>
                  
                  <div className="space-y-4">
                    {tripPayments.map((payment, index) => {
                      const pStatus = payment.status || (payment.is_confirmed ? 'approved' : 'approved');
                      const isPending = pStatus === 'pending_approval';
                      const isRejected = pStatus === 'rejected';
                      return (
                      <div
                        key={payment.id}
                        className={`border rounded-lg p-4 ${isPending ? 'border-amber-300 bg-amber-50/40' : isRejected ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-gray-50'}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          
                          {/* Payment Information */}
                          <div>
                            <h5 className="font-medium text-gray-800 mb-2">💰 معلومات الدفعة:</h5>
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center flex-wrap gap-2">
                                <span className="text-gray-600">رقم الوصل:</span>
                                <strong className="text-purple-600">{payment.payment_no}</strong>
                                {/* 🆕 inline status pill next to the receipt number,
                                    visible to everyone — alerts the accountant
                                    at a glance that this installment is pending. */}
                                {isPending && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-200 text-amber-900 border border-amber-300">
                                    ⏳ بانتظار الاعتماد
                                  </span>
                                )}
                                {isRejected && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-200 text-red-900 border border-red-300 cursor-help"
                                    title={payment.rejection_reason ? `سبب الرفض: ${payment.rejection_reason}` : ''}
                                  >
                                    ❌ مرفوضة
                                  </span>
                                )}
                                {!isPending && !isRejected && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-100 text-green-800 border border-green-300">
                                    ✅ معتمدة
                                  </span>
                                )}
                              </div>
                              <div>
                                <span className="text-gray-600">المبلغ:</span>
                                <strong className="ml-2 text-green-600">{payment.amount?.toLocaleString()} دج</strong>
                              </div>
                              <div>
                                <span className="text-gray-600">تاريخ الدفع:</span>
                                <strong className="ml-2">{new Date(payment.payment_date).toLocaleDateString('ar-DZ')}</strong>
                              </div>
                              <div>
                                <span className="text-gray-600">طريقة الدفع:</span>
                                <strong className="ml-2">
                                  {payment.payment_method === 'cash' ? '💵 نقدي' :
                                   payment.payment_method === 'bank' ? '🏦 بنكي' : '💳 بطاقة'}
                                </strong>
                              </div>
                              <div>
                                <span className="text-gray-600">مسجل بواسطة:</span>
                                <strong className="ml-2">{payment.recorded_by_name}</strong>
                              </div>
                            </div>
                          </div>

                          {/* Document Display */}
                          <div>
                            <h5 className="font-medium text-gray-800 mb-2">📄 وثيقة الوصل:</h5>
                            {payment.receipt_document_url ? (
                              <div className="flex items-center justify-center">
                                {payment.document_type === 'pdf' ? (
                                  <div 
                                    className="w-20 h-20 bg-red-50 rounded-lg flex items-center justify-center cursor-pointer border border-red-200 hover:bg-red-100 transition-colors"
                                    onClick={() => viewDocument(payment.receipt_document_url, 'pdf', payment.payment_no)}
                                    title="انقر لمشاهدة ملف PDF"
                                  >
                                    <div className="text-center">
                                      <span className="text-red-600 text-xl">📄</span>
                                      <p className="text-xs text-red-800 mt-1">PDF</p>
                                    </div>
                                  </div>
                                ) : (
                                  <img 
                                    src={`${API}${payment.receipt_document_url}`}
                                    alt={`وثيقة الدفعة ${payment.payment_no}`}
                                    className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => viewDocument(payment.receipt_document_url, 'image', payment.payment_no)}
                                    title="انقر لمشاهدة الصورة"
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-gray-400">
                                <span className="text-2xl">📄</span>
                                <p className="text-xs mt-1">لا توجد وثيقة</p>
                              </div>
                            )}
                          </div>

                          {/* Notes & Actions */}
                          <div>
                            <h5 className="font-medium text-gray-800 mb-2">📝 ملاحظات وإجراءات:</h5>
                            <div className="space-y-2">
                              {payment.notes && (
                                <div className="bg-brand-50 p-2 rounded text-xs">
                                  <strong>الملاحظات:</strong><br/>
                                  {payment.notes}
                                </div>
                              )}
                              
                              <div className="flex flex-col gap-2 mt-2">
                                {/* 🆕 Approve/Reject buttons — only on
                                    pending installments and only for non-creators.
                                    Bigger, filled style so they stand out. */}
                                {isPending && canApprove && payment.recorded_by !== user?.id && (
                                  <div className="grid grid-cols-2 gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => approveTripPayment(payment)}
                                      className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-md"
                                      data-testid={`approve-trip-payment-${payment.id}`}
                                    >
                                      ✅ اعتماد
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => rejectTripPayment(payment)}
                                      className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-md"
                                      data-testid={`reject-trip-payment-${payment.id}`}
                                    >
                                      ❌ رفض
                                    </Button>
                                  </div>
                                )}

                                {/* Print = always visible. Solid blue so it's
                                    obviously clickable (the old outline style
                                    was invisible on the gray card background). */}
                                <Button
                                  size="sm"
                                  onClick={() => askPdfOrXlsx(`${API}/trip-payments/${payment.id}/print`)}
                                  className="bg-sky-600 hover:bg-sky-700 text-white font-bold shadow-md w-full"
                                  data-testid={`print-trip-payment-${payment.id}`}
                                >
                                  🖨️ طباعة الوصل
                                </Button>

                                {/* Delete = only accountant/admin. Solid red. */}
                                {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                                  <Button
                                    size="sm"
                                    onClick={() => deletePayment(payment)}
                                    className="bg-red-500 hover:bg-red-600 text-white font-bold shadow-md w-full"
                                    data-testid={`delete-trip-payment-${payment.id}`}
                                  >
                                    🗑️ حذف الدفعة
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* 🆕 Subsequent adjustments (credit notes) attached to this receipt */}
                          <SubsequentAdjustmentsPanel receiptId={payment.id} compact />
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 🆕 Booking-level adjustments (credit notes not tied to a specific receipt) */}
              {selectedTripPayments && (
                <SubsequentAdjustmentsPanel bookingId={selectedTripPayments.id} />
              )}

              <div className="flex justify-end space-x-2 border-t pt-4">
                <Button variant="outline" onClick={() => setShowPaymentDetailsDialog(false)}>
                  إغلاق
                </Button>
                <Button 
                  onClick={() => {
                    setShowPaymentDetailsDialog(false);
                    setSelectedTripForPayment(selectedTripPayments);
                    setShowPaymentDialog(true);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  ➕ إضافة دفعة جديدة
                </Button>
                {/* 🆕 Grant exceptional discount — visible only when the file is fully
                    settled. Backend will also enforce the rule. */}
                {selectedTripPayments
                 && (selectedTripPayments.remaining_amount || 0) <= 0.01 && (
                  <Button
                    onClick={() => {
                      setCreditNoteFor({
                        id: selectedTripPayments.id,
                        label: selectedTripPayments.trip_no,
                      });
                      setShowPaymentDetailsDialog(false);
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="grant-credit-note-btn"
                  >
                    💸 منح تخفيض استثنائي
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Trips Summary Report Dialog */}
      <Dialog open={showTripsReportDialog} onOpenChange={setShowTripsReportDialog}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📊 تقرير الرحلات المنظمة</DialogTitle>
            <DialogDescription>
              تقرير شامل للرحلات مع المبالغ الكاملة والمدفوعة والمتبقية
            </DialogDescription>
          </DialogHeader>
          
          {tripsReportData && (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="bg-brand-50 p-4 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-2">معلومات التقرير:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>المُنشئ:</strong> {tripsReportData.generated_by}</div>
                  <div><strong>تاريخ الإنشاء:</strong> {new Date(tripsReportData.generated_at).toLocaleDateString('ar-DZ')}</div>
                  <div><strong>الفترة:</strong> {tripsReportData.report_period?.start_date || 'غير محدد'} - {tripsReportData.report_period?.end_date || 'غير محدد'}</div>
                  <div><strong>نوع الرحلة:</strong> {tripsReportData.report_period?.trip_type_filter === 'all' ? 'جميع الأنواع' : tripsReportData.report_period?.trip_type_filter || 'جميع الأنواع'}</div>
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-2xl font-bold text-brand-600">{tripsReportData.summary?.total_trips || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي الرحلات</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-green-600">{tripsReportData.summary?.total_final_amounts?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي المبالغ (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-brand-600">{tripsReportData.summary?.total_paid_amounts?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي المدفوع (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-orange-600">{tripsReportData.summary?.total_remaining_amounts?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي المتبقي (دج)</div>
                  </CardContent>
                </Card>
              </div>

              {/* By Trip Type */}
              {Object.keys(tripsReportData.by_trip_type || {}).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>📊 ملخص حسب نوع الرحلة</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(tripsReportData.by_trip_type).map(([tripType, data]) => (
                        <div key={tripType} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">
                              {tripType === 'عمرة' ? '🕋' : tripType === 'خارجية' ? '🌍' : '🏠'} {tripType}
                            </span>
                            <span className="text-sm text-gray-600">{data.count} رحلة</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">المبلغ الإجمالي:</span>
                              <div className="font-bold text-green-600">{data.total_amount?.toLocaleString()} دج</div>
                            </div>
                            <div>
                              <span className="text-gray-600">المدفوع:</span>
                              <div className="font-bold text-brand-600">{data.paid_amount?.toLocaleString()} دج</div>
                            </div>
                            <div>
                              <span className="text-gray-600">المتبقي:</span>
                              <div className="font-bold text-orange-600">{data.remaining_amount?.toLocaleString()} دج</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowTripsReportDialog(false)}>
                  إغلاق
                </Button>
                <Button onClick={() => window.print()} className="bg-brand-600 hover:bg-brand-700">
                  🖨️ طباعة التقرير
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjustments Report Dialog */}
      <Dialog open={showAdjustmentsReportDialog} onOpenChange={setShowAdjustmentsReportDialog}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📈 تقرير التخفيضات والعمولات</DialogTitle>
            <DialogDescription>
              تقرير مفصل للتخفيضات والعمولات مع المطبقين والأسباب
            </DialogDescription>
          </DialogHeader>
          
          {adjustmentsReportData && (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-bold text-purple-800 mb-2">معلومات التقرير:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>المُنشئ:</strong> {adjustmentsReportData.generated_by}</div>
                  <div><strong>تاريخ الإنشاء:</strong> {new Date(adjustmentsReportData.generated_at).toLocaleDateString('ar-DZ')}</div>
                  <div><strong>الفترة:</strong> {adjustmentsReportData.report_period?.start_date || 'غير محدد'} - {adjustmentsReportData.report_period?.end_date || 'غير محدد'}</div>
                  <div><strong>نوع التعديل:</strong> {adjustmentsReportData.report_period?.adjustment_type_filter === 'all' ? 'جميع الأنواع' : adjustmentsReportData.report_period?.adjustment_type_filter || 'جميع الأنواع'}</div>
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-purple-600">{adjustmentsReportData.summary?.total_adjustments || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي التعديلات</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-red-600">{adjustmentsReportData.summary?.total_discounts?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">مجموع التخفيضات (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-green-600">{adjustmentsReportData.summary?.total_commissions?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">مجموع العمولات (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-brand-600">{adjustmentsReportData.summary?.net_adjustment?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-600">الصافي النهائي (دج)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <div className="text-sm font-bold text-gray-600">{adjustmentsReportData.summary?.discounts_count || 0} / {adjustmentsReportData.summary?.commissions_count || 0}</div>
                    <div className="text-xs text-gray-600">تخفيضات / عمولات</div>
                  </CardContent>
                </Card>
              </div>

              {/* By Employee Summary */}
              {Object.keys(adjustmentsReportData.by_employee || {}).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>👥 ملخص حسب الموظف/المحاسب</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(adjustmentsReportData.by_employee).map(([employeeName, data]) => (
                        <div key={employeeName} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">{employeeName}</span>
                            <span className="text-sm text-gray-600">{data.total_adjustments} تعديل</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-red-600">🔽 التخفيضات:</span>
                              <div className="font-bold">{data.discounts_count} ({data.discounts_amount?.toLocaleString()} دج)</div>
                            </div>
                            <div>
                              <span className="text-green-600">🔼 العمولات:</span>
                              <div className="font-bold">{data.commissions_count} ({data.commissions_amount?.toLocaleString()} دج)</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detailed Adjustments List */}
              {adjustmentsReportData.adjustments_details && adjustmentsReportData.adjustments_details.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>📋 قائمة التعديلات التفصيلية</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-right">التاريخ</TableHead>
                            <TableHead className="text-right">رقم الرحلة</TableHead>
                            <TableHead className="text-right">العميل</TableHead>
                            <TableHead className="text-right">الخدمة/الوجهة</TableHead>
                            <TableHead className="text-right">نوع التعديل</TableHead>
                            <TableHead className="text-right">المبلغ (دج)</TableHead>
                            <TableHead className="text-right">السبب</TableHead>
                            <TableHead className="text-right">المطبق بواسطة</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {adjustmentsReportData.adjustments_details.map((adjustment) => (
                            <TableRow key={adjustment.id}>
                              <TableCell className="text-right">
                                {new Date(adjustment.applied_at).toLocaleDateString('ar-DZ')}
                              </TableCell>
                              <TableCell className="text-right font-medium text-brand-600">
                                {adjustment.trip_no}
                              </TableCell>
                              <TableCell className="text-right">
                                {adjustment.trip_context?.client_name || 'غير محدد'}
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {adjustment.trip_context?.service_name}<br/>
                                <span className="text-gray-500">{adjustment.trip_context?.destination}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  adjustment.adjustment_type === 'discount' 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {adjustment.adjustment_type === 'discount' ? '🔽 تخفيض' : '🔼 عمولة'}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                <span className={adjustment.adjustment_type === 'discount' ? 'text-red-600' : 'text-green-600'}>
                                  {adjustment.adjustment_type === 'discount' ? '-' : '+'} {adjustment.amount?.toLocaleString()} دج
                                </span>
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {adjustment.reason}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {adjustment.applied_by_name}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowAdjustmentsReportDialog(false)}>
                  إغلاق
                </Button>
                <Button onClick={() => window.print()} className="bg-purple-600 hover:bg-purple-700">
                  🖨️ طباعة التقرير
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 UNIFIED CONFIRM DIALOG (replaces Chrome's blockable
          window.confirm / window.prompt). Used for approve/reject of
          trips and trip-payments + the delete flow. */}
      <Dialog
        open={!!confirmDialog}
        onOpenChange={(o) => { if (!o && !confirmBusy) { setConfirmDialog(null); setConfirmReason(''); } }}
      >
        <DialogContent className="max-w-md" data-testid="unified-confirm-dialog">
          <DialogHeader>
            <DialogTitle className={confirmDialog?.danger ? 'text-red-700' : 'text-amber-800'}>
              {confirmDialog?.title}
            </DialogTitle>
            <DialogDescription className="text-right pt-2 text-sm leading-relaxed text-gray-700">
              {confirmDialog?.message}
            </DialogDescription>
          </DialogHeader>

          {confirmDialog?.askReason && (
            <div className="space-y-2 pt-1">
              <Label htmlFor="confirm-reason-input" className="text-sm font-semibold">
                سبب الرفض (مطلوب — 3 أحرف على الأقل):
              </Label>
              <Textarea
                id="confirm-reason-input"
                data-testid="confirm-reason-input"
                autoFocus
                dir="rtl"
                rows={3}
                value={confirmReason}
                onChange={(e) => setConfirmReason(e.target.value)}
                placeholder="مثال: المبلغ غير صحيح، أعد التحقق..."
                disabled={confirmBusy}
              />
            </div>
          )}

          <DialogFooter className="gap-2 pt-3">
            <Button
              variant="outline"
              onClick={() => { setConfirmDialog(null); setConfirmReason(''); }}
              disabled={confirmBusy}
              data-testid="confirm-cancel-btn"
            >
              إلغاء
            </Button>
            <Button
              onClick={executeConfirm}
              disabled={confirmBusy || (confirmDialog?.askReason && confirmReason.trim().length < 3)}
              className={confirmDialog?.danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
              data-testid="confirm-execute-btn"
            >
              {confirmBusy ? '⏳ جاري التنفيذ...' : confirmDialog?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🤝 Subagent supplier-picker dialog (B2B partner approvals) */}
      <SubagentApprovalDialog
        open={!!subagentApprovalTarget}
        onClose={() => setSubagentApprovalTarget(null)}
        onConfirm={handleSubagentApprovalConfirm}
        itemLabel={subagentApprovalTarget?.itemLabel}
        agencyName={subagentApprovalTarget?.agencyName}
        sellPrice={subagentApprovalTarget?.sellPrice}
        loading={subagentApprovalBusy}
      />

      {/* 🆕 Grant Credit Note (exceptional discount after full settlement) */}
      {creditNoteFor && (
        <GrantCreditNoteDialog
          bookingType="organized_trip"
          bookingId={creditNoteFor.id}
          bookingLabel={creditNoteFor.label}
          onClose={() => setCreditNoteFor(null)}
          onCreated={() => { setCreditNoteFor(null); fetchTrips(); }}
        />
      )}
    </div>
  );
});

// ====================================================================================================
// NEW: QURANIC VERSES MANAGEMENT COMPONENT - مكون إدارة الآيات القرآنية
// ====================================================================================================


export default OrganizedTripsManagement;
